import { useDarkMode } from "@/hooks/useDarkMode";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { useLoadGoogleMaps } from "@/hooks/useLoadGoogleMaps";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  mapCenterAtom,
  mapDetailOriginAtom,
  mobileListViewAtom,
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  savedPlaceSortByToViewAtom,
  hoveredVenueIdAtom,
  viewModeAtom,
  pendingGooglePlaceIdAtom,
  mapViewportAtom,
  fitMapToResultsAtom,
  locationSearchAnchorAtom,
  pinnedFirstVenueAtom,
  focusPreviewAtom,
} from "@/state/atoms";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import {
  APIProvider,
  Map as VisGlMap,
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  InfoWindow,
  useMap,
  type MapMouseEvent as VisGlMapMouseEvent,
} from "@vis.gl/react-google-maps";
import {
  defaultRegion,
  darkModeStyles,
  lightModeStyles,
  enableAutoZoom,
  enableGooglePoiClick,
  enableAdvancedMarkers,
  googleMapKey,
  googleMapId,
  googleMapIdPoi,
  googleMapDarkModeIdPoi,
} from "common/config";
import {
  AdvancedPinContent,
  SIZE_DEFAULT as PIN_SIZE_DEFAULT,
  SIZE_SELECTED as PIN_SIZE_SELECTED,
  SIZE_ACTIVE as PIN_SIZE_ACTIVE,
  PIN_ASPECT,
  NAME_HALO,
  NAME_TEXT_COLOR,
  CATEGORY_TEXT_COLOR,
  getPinCentreTop,
} from "@/components/Map/AdvancedPinContent";
import { getPinColor, getVenuePinColor } from "@/components/Map/pinColors";
import { PoiContributionCard } from "@/components/Map/PoiContributionCard";
import { ContributionOptionsModal } from "@/components/Map/ContributionOptionsModal";
import { MapModeToggle } from "@/components/Map/MapModeToggle";
import { AllPlacesHint } from "@/components/Map/AllPlacesHint";
import { allPlacesHintDismissedAtom } from "@/store/atoms";
import { useMapMode } from "@/components/Map/useMapMode";
import { MarkerClusterer, SuperClusterAlgorithm, type Cluster } from "@googlemaps/markerclusterer";
import { createPlaceFromGoogle } from "common/apis";
import { getPinIcon as resolveVenuePinType, hasVenueData } from "common/utils/maps";
import { Storage } from "common/storage";
import { trackEvent } from "common/analytics";
import { pinTypeForVenue, parseFocusPlaceIds } from "common/utils";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";
import {
  useSearchPageParams,
  useHomeVenues,
  useI18N,
  useMapSettings,
  useSavedPlaces,
  useAuth,
} from "common/hooks";
import { Venue, VenuePinType } from "common/types";
import { calculateZoomFromDistance } from "common/apis/search";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useMatchRoute } from "@tanstack/react-router";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";

import GreyPin from "@/assets/pins/grey_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import BluePin from "@/assets/pins/blue_pin.svg";
import BlackPin from "@/assets/pins/black_pin.svg";
import GreenPin from "@/assets/pins/green_pin.svg";
import PurplePin from "@/assets/pins/purple_pin.svg";
import YouAre from "@/assets/pins/youAre.svg";
import CentreLocation from "@/assets/pins/centreLocation.svg";
import VenueCard from "@/components/VenueCard/VenueCard";
import { FiMapPin } from "react-icons/fi";
import { cn } from "@/lib/utils";
import { TbCurrentLocation } from "react-icons/tb";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { XIcon } from "@phosphor-icons/react";
import Loader from "@/components/common/Loader";
import ModalWithAction from "@/components/common/ModalWithAction";
import { StandardButton } from "@/components/common/StandardButton";
import Modal from "react-responsive-modal";
import CreateList from "@/components/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/SavedPlaces/AddRemoveVenueToList";

/**
 * Captures the vis.gl map instance and forwards it to the parent component's
 * mapRef state so all existing mapRef.setZoom / mapRef.getCenter calls work
 * unchanged in the Advanced Markers path.
 */
function MapRefCapture({ onMap }: { onMap: (map: google.maps.Map) => void }) {
  const map = useMap();
  React.useEffect(() => {
    if (map) onMap(map);
  }, [map, onMap]);
  return null;
}

// Stable reference so vis.gl InfoWindow never sees a changed pixelOffset between renders.
const SNIPPET_PIXEL_OFFSET: [number, number] = [0, -50];

// Pin ladder: clusters -> dots -> full pins.
const DESKTOP_QUERY = "(min-width: 1024px)";
const CLUSTER_RADIUS = 80; // px: markers within this distance merge into a cluster
const CLUSTER_MAX_ZOOM = 16.5; // clusters active up to (and including) this zoom
// Selected/focused pin must sit above cluster bubbles (2_000_000 + count).
const SELECTED_MARKER_ZINDEX = 3_000_000;
// User's current-location dot must always sit above everything else, including
// selected pins and cluster bubbles.
const USER_LOCATION_ZINDEX = 4_000_000;
// Centre-location marker sits above everything, including cluster bubbles.
const CENTRE_LOCATION_ZINDEX = 5_000_000;
const SOCIABILITY_DOT_START_ZOOM = 16.5; // ranked dots start flipping to pins here (<=16.5 = clusters+dots only)
const SOCIABILITY_PIN_MIN_ZOOM = 19; // every venue is a full pin from here up
// Top slice already shown as pins at SOCIABILITY_DOT_START_ZOOM (no dots-only level).
const SOCIABILITY_PIN_HEAD_FRACTION = 0.35;
// Few-result searches don't need decluttering — show full pins at every zoom
// (skip the cluster/dot phases) when the result count is at/below this.
const FEW_RESULTS_PIN_THRESHOLD = 12;
// Labels only once full pins show, so no label floats over a dot/cluster.
// Same zoom across all modes, since that's when every venue is a full pin.
const LABEL_MIN_ZOOM = SOCIABILITY_PIN_MIN_ZOOM;
// Minimum on-screen gap (px, at the current zoom) between two Sociability
// labels. Google's own collision only sees each label marker's small anchor
// box (the visible text is absolutely-positioned outside it), so it rarely
// detects real overlap — this pixel-distance pass thins labels ourselves,
// closer to native's hand-rolled declutter.
const LABEL_MIN_SEPARATION_PX = 90;
// Overview mode: all dots below this zoom, all pins at/above it. In between,
// most-reviewed venues flip to pins first as you zoom in.
const OVERVIEW_TRANSITION_START_ZOOM = 16;
const OVERVIEW_PIN_MIN_ZOOM = 19;
const OVERVIEW_DOT_SIZE = 16;
// Invisible hit-area around the dot, for easier hover/tap.
const OVERVIEW_DOT_HIT_SIZE = 26;

// Cluster pin artwork, in the pins' own orange so a cluster reads as the pins it
// stands in for. Coordinates are the design's; the halo is drawn as an outside
// stroke under the fill rather than Figma's mask, which looks the same.
const CLUSTER_PIN_PATH =
  "M43 10C53.0139 10 61.1318 18.1179 61.1318 28.1318C61.1318 34.1822 58.1679 39.5392 53.6133 42.833L44.4297 50.6992C43.6068 51.404 42.3932 51.404 41.5703 50.6992L32.3818 42.8301C27.8301 39.536 24.8682 34.18 24.8682 28.1318C24.8682 18.118 32.9862 10.0001 43 10Z";
const CLUSTER_GLYPH_PATH =
  "M43 20.125C41.3595 20.1269 39.7868 20.7794 38.6268 21.9393C37.4669 23.0993 36.8144 24.672 36.8125 26.3125C36.8125 31.607 42.4375 35.6057 42.6773 35.773C42.7718 35.8393 42.8845 35.8748 43 35.8748C43.1155 35.8748 43.2282 35.8393 43.3227 35.773C43.5625 35.6057 49.1875 31.607 49.1875 26.3125C49.1856 24.672 48.5331 23.0993 47.3732 21.9393C46.2132 20.7794 44.6405 20.1269 43 20.125ZM43 24.0625C43.445 24.0625 43.88 24.1945 44.25 24.4417C44.62 24.6889 44.9084 25.0403 45.0787 25.4515C45.249 25.8626 45.2936 26.315 45.2068 26.7515C45.12 27.1879 44.9057 27.5888 44.591 27.9035C44.2763 28.2182 43.8754 28.4325 43.439 28.5193C43.0025 28.6061 42.5501 28.5615 42.139 28.3912C41.7278 28.2209 41.3764 27.9325 41.1292 27.5625C40.882 27.1925 40.75 26.7575 40.75 26.3125C40.75 25.7158 40.9871 25.1435 41.409 24.7215C41.831 24.2996 42.4033 24.0625 43 24.0625Z";
// Pin plus its 4px halo, so the artwork fills the box with no dead space.
const CLUSTER_PIN_VIEWBOX = "20.868 6 44.264 49.4";
const CLUSTER_PIN_ASPECT = 49.4 / 44.264;

const makeClusterRenderer = (isDark: boolean) => ({
  render: ({
    count,
    position,
  }: {
    count: number;
    position: google.maps.LatLng | google.maps.LatLngLiteral;
  }) => {
    const clusterColor = getPinColor(VenuePinType.Orange, isDark);
    const glyphColor = isDark ? "#141415" : "#FAFAFA";
    const shadowColor = isDark ? "250,250,250" : "7,7,8";
    // Size still scales with density, the one signal a countless pin keeps.
    const width = count < 10 ? 40 : count < 100 ? 48 : 56;
    const wrapper = document.createElement("div");
    // display:flex keeps the inline-svg baseline gap from lifting the tip off the point.
    wrapper.style.cssText = `display:flex;filter:drop-shadow(0 2px 6px rgba(${shadowColor},0.12)) drop-shadow(0 1px 2px rgba(${shadowColor},0.08));`;
    wrapper.innerHTML = `<svg width="${width}" height="${
      width * CLUSTER_PIN_ASPECT
    }" viewBox="${CLUSTER_PIN_VIEWBOX}" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="${CLUSTER_PIN_PATH}" stroke="#E66000" stroke-opacity="0.4" stroke-width="8"/><path d="${CLUSTER_PIN_PATH}" fill="${clusterColor}"/><path d="${CLUSTER_GLYPH_PATH}" fill="${glyphColor}"/></svg>`;
    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content: wrapper,
      // Above every pin/label priority (selected pins use 1_000_000).
      zIndex: 2_000_000 + count,
      gmpClickable: true,
    });
  },
});

// Cap so a tight cluster doesn't fitBounds all the way to max zoom.
const CLUSTER_CLICK_MAX_ZOOM = 18;

// Zoom into a cluster on click: fit its extent with padding, capped.
const handleClusterClick = (
  _event: google.maps.MapMouseEvent,
  cluster: Cluster,
  map: google.maps.Map,
) => {
  if (cluster.bounds) {
    map.fitBounds(cluster.bounds, 80);
    const z = map.getZoom();
    if (typeof z === "number" && z > CLUSTER_CLICK_MAX_ZOOM) {
      map.setZoom(CLUSTER_CLICK_MAX_ZOOM);
    }
  } else {
    const z = map.getZoom() ?? CLUSTER_MAX_ZOOM;
    map.setZoom(Math.min(z + 2, CLUSTER_CLICK_MAX_ZOOM));
    map.panTo(cluster.position);
  }
};

// Overview mode's low-zoom dot: colored circle, hoverable to reveal the venue name.
function OverviewDot({
  color,
  name,
  isHovered,
  onMouseEnter,
  onMouseLeave,
}: {
  color: string;
  name?: string;
  isHovered?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  return (
    <div
      role="button"
      aria-label={name || "Map dot"}
      className="relative flex items-center justify-center cursor-pointer"
      style={{
        width: OVERVIEW_DOT_HIT_SIZE,
        height: OVERVIEW_DOT_HIT_SIZE,
        transform: `translateY(${OVERVIEW_DOT_HIT_SIZE / 2}px)`,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        aria-hidden="true"
        style={{
          width: OVERVIEW_DOT_SIZE,
          height: OVERVIEW_DOT_SIZE,
          borderRadius: "9999px",
          background: color,
          border: "1.5px solid #ffffff",
          boxShadow: "0 1px 2px rgba(0,0,0,0.35)",
          transform: isHovered ? "scale(1.4)" : undefined,
          transition: "transform 120ms ease-out",
        }}
      />
      {name && (
        <div
          className={cn(
            "absolute left-full top-1/2 ml-1 -translate-y-1/2 pointer-events-none select-none transition-opacity duration-150",
            isHovered ? "opacity-100" : "opacity-0",
          )}
        >
          <span
            className="block text-[13px] font-medium leading-tight whitespace-nowrap max-w-[160px] truncate"
            style={{ textShadow: NAME_HALO, color: NAME_TEXT_COLOR }}
          >
            {name}
          </span>
        </div>
      )}
    </div>
  );
}

// BasicMapView mounts twice (desktop + mobile). Dedupe "selectGooglePlace"
// across both instances (module-scoped = shared).
let lastHandledSelectGooglePlaceRequestId: string | null = null;
let lastHandledSelectGooglePlaceAt = 0;
const SELECT_GOOGLE_PLACE_DEDUPE_WINDOW_MS = 2000;

function BasicMapView(props: {
  query: string;
  hidden?: boolean;
  currentVenueTypePage?: number;
  mapSource?: string;
  locations?: Venue[];
  // When provided (search page), the POI card's "Get Alerts" opens the host's
  // notification popup in place instead of navigating to the venue detail page.
  onGetNotifiedForVenue?: (venue: Venue) => void;
}) {
  const { currentVenueTypePage = 0, locations: locationsOverride } = props;
  // Kept fresh for async callbacks whose closures would otherwise go stale.
  const currentVenueTypePageRef = useRef(currentVenueTypePage);
  useEffect(() => {
    currentVenueTypePageRef.current = currentVenueTypePage;
  }, [currentVenueTypePage]);
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [savedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const [savedPlaceSortByToView] = useAtom(savedPlaceSortByToViewAtom);

  const i18n = useI18N();
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const { mapSettings } = useMapSettings();

  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [tooltipVenue, setTooltipVenue] = useState<Venue | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  const [snippetPosition, setSnippetPosition] = useState<{
    offset: { x: number; y: number };
    className: string;
  } | null>(null);
  const [snippetOpen, setSnippetOpen] = useState<boolean>(false); // Track if snippet is actively displayed
  // TODO: Panning state commented out since we're not using map panning
  // const [isPanning, setIsPanning] = useState(false);
  // Cooldown to suppress auto-zoom right after a user-initiated map move
  const userMovedMapAtRef = useRef<number>(0);

  // Track if we've set the initial user location to prevent duplicate API calls
  // When navigating to search page with coordinates already set, we don't want to
  // immediately update userlatitude/userlongitude and trigger another API call
  const hasSetInitialUserLocationRef = useRef(false);
  // When user clicks "Go to current location", the next userLocation update should
  // force a full reset of search center (latitude/longitude + unixTimestamp)
  const goToUserLocationRequestedRef = useRef(false);

  // Track if map has completed initial load to prevent onMapMoved from firing during map initialization
  // This prevents the map from overwriting synced location params when the idle event fires on initial map load
  const mapHasCompletedInitialLoadRef = useRef(false);
  // Track the last location params that the map was initialized with
  // Only allow onMapMoved to update location if the current params match the initialized params
  const lastInitializedParamsRef = useRef<{
    lat: string | null;
    lng: string | null;
  }>({ lat: null, lng: null });
  // Zoom at the time of the last reload — used to measure accumulated zoom delta
  // (not updated on every idle, only when a reload is triggered or on init)
  const lastReloadedZoomRef = useRef<number | null>(null);
  // Debounce timer for progressive search — clears rapid zoom steps and mid-pan idles
  const progressiveSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set right before a deliberate "reload nearby" (e.g. auto-suggest pick) clears
  // map bounds, so the next map-init cycle it triggers doesn't immediately reapply
  // the default tight viewport + mapped-only filter on top of that wide reload.
  const suppressNextInitBoundsRef = useRef(false);

  const { selectedVenue, setSelectedVenue } = useSelectedVenue();

  const [, setMobileListView] = useAtom(mobileListViewAtom);
  const [, setDetailOrigin] = useAtom(mapDetailOriginAtom);
  const [hoveredVenueId, setHoveredVenueId] = useAtom(hoveredVenueIdAtom);

  const { isMapsLoaded } = useLoadGoogleMaps();

  const [mapCenter, setMapCenter] = useAtom(mapCenterAtom);
  const setMapViewport = useSetAtom(mapViewportAtom);
  const [locationSearchAnchor, setLocationSearchAnchor] = useAtom(
    locationSearchAnchorAtom,
  );
  const pendingGooglePlaceId = useAtomValue(pendingGooglePlaceIdAtom);
  const setPendingGooglePlaceId = useSetAtom(pendingGooglePlaceIdAtom);
  const fitMapToResults = useAtomValue(fitMapToResultsAtom);
  const pinnedFirstVenue = useAtomValue(pinnedFirstVenueAtom);
  const setPinnedFirstVenue = useSetAtom(pinnedFirstVenueAtom);
  const setFocusPreview = useSetAtom(focusPreviewAtom);
  
  const isDark = useDarkMode();
  const { user, isMapper, isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();
  // Pending "Get Alerts" from the POI card while the user logs in — resumed after.
  const pendingGetNotifiedRef = useRef(false);
  const [viewMode] = useAtom(viewModeAtom);
  const isMapperViewActive = isMapper && viewMode === "mapper";

  // CheckIcon if we're on the /search route to conditionally skip certain effects
  const matchRoute = useMatchRoute();
  const isSearchRoute = matchRoute({ to: "/search" });

  const { data, refetch } = useSavedPlaces();
  const savedPlaces = data?.saved_places || [];

  const markerRefs = useRef(new Map());

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const [locationForSavedPlaces, setLocationForSavedPlaces] = useState(0);
  const [listNameCreated, setListNameCreated] = useState("");

  // Venues resolved from clicking native Google POIs. Persisted on the map until
  // the next search so re-clicking an already-resolved POI doesn't refetch.
  const [resolvedPois, setResolvedPois] = useState<Venue[]>([]);
  // Message card at the clicked point for unsupported / error cases.
  const [poiMessage, setPoiMessage] = useState<{
    lat: number;
    lng: number;
    text: string;
    // "unsupported" also shows a selected grey pin (like the no-data card).
    variant?: "unsupported" | "error";
  } | null>(null);
  // Lightweight "resolving a tapped place" indicator (non-blocking pill, not the
  // full-screen loader) while place-from-google runs.
  const [poiResolving, setPoiResolving] = useState(false);
  // Opt-in contribution flow for a clicked Google POI that's supported but not yet
  // in our DB (All-places mode only): intro -> options (multi-select) -> create.
  // "unsupported" is a terminal message for categories we don't cover.
  const [poiContribution, setPoiContribution] = useState<{
    step: "intro" | "options" | "unsupported";
    placeId: string;
    lat: number;
    lng: number;
    name?: string;
    /** Supported-but-not-yet-created POI: Google's business type, for the card's badge only. */
    businessType?: string;
    /** Google's label for an unsupported category. */
    category?: string;
    // When set, the venue already exists in our DB (a grey pin with no
    // accessibility data). "Continue" opens it instead of creating a new one.
    venue?: Venue;
    selected: string[];
    creating: boolean;
  } | null>(null);
  // place_ids in-flight, to dedupe calls from concurrent event sources (map click + selectGooglePlace).
  const resolvingPlaceIds = useRef<Set<string>>(new Set());

  // Listen for custom force zoom event
  useEffect(() => {
    if (mapRef) {
      const handleForceZoom = (
        event: CustomEvent<{ zoom: number; lat: number; lng: number }>,
      ) => {
        const { zoom, lat, lng } = event.detail;
        console.log("Force zoom event received:", zoom, lat, lng);

        // Set the map center and zoom level
        mapRef.setCenter({ lat, lng });
        mapRef.setZoom(zoom);
      };

      window.addEventListener("forceMapZoom", handleForceZoom as EventListener);

      return () => {
        window.removeEventListener(
          "forceMapZoom",
          handleForceZoom as EventListener,
        );
      };
    }
  }, [mapRef]);

  // Sync saved places modal mode with saved places data
  useEffect(() => {
    if (savedPlaces?.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  }, [savedPlaces?.length, user]);

  // Close snippet when the map is zoomed. Panning leaves it open — it is
  // anchored to the venue's coordinates, so it travels with the map.
  useEffect(() => {
    if (mapRef) {
      const closeSnippet = () => {
        setActiveMarker(null);
        setSnippetPosition(null);
        setTooltipVenue(null);
        setHoveredMarkerId(null);
        setSnippetOpen(false);
      };

      const zoomChangedListener = mapRef.addListener(
        "zoom_changed",
        closeSnippet,
      );

      return () => {
        if (zoomChangedListener) {
          google.maps.event.removeListener(zoomChangedListener);
        }
      };
    }
  }, [mapRef]);

  const handleSetLocation = async () => {
    //set user location
    // console.log("inside handleSetLocation");
    setIsLocationModalOpen(false);
    return;
  };

  // Function to show snippet only (no venue loading)
  const handleShowSnippet = (marker: string) => {
    if (marker === activeMarker && snippetOpen) {
      return;
    }

    // Calculate and cache snippet position FIRST before any state changes
    const venue = locations.find((l) => l.id === marker);
    if (!venue) return;

    const pos = getSnippetPosition(venue);

    // Now update all states at once to minimize re-renders
    setTooltipVenue(null);
    setHoveredMarkerId(marker);
    setHoveredVenueId(marker);
    setSnippetPosition(pos);
    setActiveMarker(marker);
    setSnippetOpen(true); // Mark snippet as open
    setLocationSearchAnchor(null);
    // A grey-pin "Add info" snippet may still be open from a previous pin;
    // it lives in separate state and won't close itself when another pin's
    // snippet opens.
    setPoiContribution(null);
  };

  // Function to load venue in left panel (called from snippet click)
  const handleLoadVenue = (location: Venue) => {
    // Complex venue: open in new tab (matches list click behaviour)
    if (
      location.business?.templateId != null &&
      location.business?.templateId > 1 &&
      location.business?.publicUrl &&
      location.business.publicUrl !== ""
    ) {
      window.open(`/venuedetails/${location.business.publicUrl}`, "_blank");
      handleCloseSnippet(); // close the snippet so the map is left clean
      return;
    }

    // Basic venue: load in side panel. The detail lives in the list container,
    // so we flip to it — but remember the user came from the map so the back
    // action can return them to the map view.
    setDetailOrigin("map");
    setMobileListView(true);
    setSelectedVenue(location);
    setActiveMarker(null);
    setSnippetPosition(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
    setSnippetOpen(false); // Close snippet
    setPoiContribution(null);
    // Note: Keep hoveredVenueId for the selected venue
  };

  // Shared pin-click behaviour (track + open snippet/venue), also used by the
  // separate name-label marker so a click still registers even if it lands on
  // the label instead of the pin (e.g. collision engine z-order edge cases).
  const triggerVenueMarkerClick = (location: Venue, index: number) => {
    trackEvent("place_pin_selected", {
      brand_id: location.brandId != null ? String(location.brandId) : "",
      brand_name: location.brandName ?? "",
      business_id: location.business?.id?.toString() ?? "",
      entry_index: index,
      pin_type: pinTypeForVenue(location),
      place_name: location?.name ?? "",
    });

    // Picking any other pin retires the searched venue's highlighted pin.
    if (!isFocusedPlace(location) && pinnedFirstVenue?.id !== location.id) {
      setFocusPinRetired(true);
    }

    if (currentVenueTypePage === 1 || props.mapSource === "venuedetails") {
      handleLoadVenue(location);
    } else {
      handleShowSnippet(location.id);
    }
  };

  // Save venue handlers
  const handleSaveVenue = (location) => {
    if (!user) {
      console.log("Please login");
      return;
    }
    console.log("in handleSaveVenue", location);
    setLocationForSavedPlaces(location?.business.id);
    setOpenSavedPlacesModal(true);
  };

  const handleSavedPlacesModalClose = () => {
    console.log(
      "Closing Modal handleSavedPlacesModalClose savePlaceListMode=",
      savePlaceListMode,
    );

    setOpenSavedPlacesModal(false);

    if (savePlaceListMode === "create") {
      //refetch the lists and open the modal with create button and all lists
      handleSaveVenueAll();
    } else {
      setLocationForSavedPlaces(0);
      setListNameCreated("");
    }
  };

  const handleSaveVenueAll = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  const handleCreateList = (listNameCreated) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  // Function to close the snippet without loading venue
  const handleCloseSnippet = () => {
    setActiveMarker(null);
    setSnippetPosition(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
    setHoveredVenueId("");
    setSnippetOpen(false); // Close snippet
  };

  // Dismiss the transient unsupported/error message card. Resolved POI markers
  // persist on the map (closing the snippet is handled by handleCloseSnippet).
  const handleClosePoi = () => {
    setPoiMessage(null);
    setPoiContribution(null);
  };

  // Handle a native Google POI click. Returns true when handled, so the caller
  // can skip the default clear behaviour.
  // Persist a resolved venue as a POI pin and open its preview card. When `center`
  // is true (e.g. from auto-suggest) the map pans to it first.
  const showResolvedVenue = (venue: Venue, center: boolean) => {
    // For resolved POIs, the pin should reflect user-contributed data only: orange
    // when the venue has any (review/experience/alert), otherwise grey. This avoids
    // showing orange for venues that are non-grey solely due to mapper-only states
    // (e.g. notes for mappers), which would otherwise render as orange in the user view.
    const hasAccessibilityData = hasVenueData(venue);
    const resolvedVenue = {
      ...venue,
      venuePinType: hasAccessibilityData
        ? resolveVenuePinType(venue)
        : VenuePinType.Grey,
    };
    setResolvedPois((prev) =>
      prev.some((v) => v.placeId === resolvedVenue.placeId)
        ? prev
        : [...prev, resolvedVenue],
    );

    // In the venue-detail context the snippet won't render, so load the venue
    // detail directly in one click (same pattern as regular pins).
    if (currentVenueTypePageRef.current === 1 || props.mapSource === "venuedetails") {
      if (center && mapRef && resolvedVenue.latitude != null && resolvedVenue.longitude != null) {
        const dest = { lat: resolvedVenue.latitude, lng: resolvedVenue.longitude };
        mapRef.panTo(dest);
        setMapCenter(dest);
      }
      handleLoadVenue(resolvedVenue);
      return;
    }

    const openSnippet = () => {
      setActiveMarker(resolvedVenue.id);
      setSnippetPosition(getSnippetPosition(resolvedVenue));
      setSnippetOpen(true);
      // This venue is the anchor now.
      setLocationSearchAnchor(null);
    };
    if (
      center &&
      mapRef &&
      resolvedVenue.latitude != null &&
      resolvedVenue.longitude != null
    ) {
      const dest = { lat: resolvedVenue.latitude, lng: resolvedVenue.longitude };
      mapRef.panTo(dest);
      // Keep mapCenterAtom in sync so auto-suggest uses the new map center
      // for distance sorting on the next search. We update it here (not in
      // onMapMoved) to avoid the re-render that was causing snippet misalignment.
      setMapCenter(dest);
      // Delay so the programmatic-move snippet-close listener fires first.
      window.setTimeout(openSnippet, 350);
    } else {
      openSnippet();
    }
  };

  // Resolve a Google place_id into a venue (POI clicks + auto-suggest): show the
  // preview card on success, or a message card on unsupported/error.
  // place_pin_selected for a native Google POI tap (parallels handleVenueMarkerClick for our own
  // markers). entry_index = list position when the POI is already a loaded result, else 0.
  const trackPoiPinSelected = (venue: Venue) => {
    const idx = locations.findIndex((l: Venue) => l.id === venue.id);
    trackEvent("place_pin_selected", {
      brand_id: venue.brandId != null ? String(venue.brandId) : "",
      brand_name: venue.brandName ?? "",
      business_id: venue.business?.id?.toString() ?? "",
      entry_index: idx >= 0 ? idx : 0,
      pin_type: pinTypeForVenue(venue),
      place_name: venue.name ?? "",
    });
  };

  const resolvePlaceAndShow = (
    placeId: string,
    opts: {
      center: boolean;
      messageLat?: number;
      messageLng?: number;
      sessionToken?: string;
    },
  ) => {
    // Already one of our loaded search pins: just show its snippet (no API call).
    const existing = locations.find((l) => l.placeId && l.placeId === placeId);
    if (existing) {
      trackPoiPinSelected(existing);
      if (
        opts.center &&
        mapRef &&
        existing.latitude != null &&
        existing.longitude != null
      ) {
        mapRef.panTo({ lat: existing.latitude, lng: existing.longitude });
        window.setTimeout(() => handleShowSnippet(existing.id), 350);
      } else {
        handleShowSnippet(existing.id);
      }
      return;
    }

    // Already resolved earlier this session: reuse it, no refetch.
    const alreadyResolved = resolvedPois.find((v) => v.placeId === placeId);
    if (alreadyResolved) {
      trackPoiPinSelected(alreadyResolved);
      showResolvedVenue(alreadyResolved, opts.center);
      return;
    }

    // Dedupe concurrent requests for the same place_id (map click + selectGooglePlace).
    if (resolvingPlaceIds.current.has(placeId)) return;
    resolvingPlaceIds.current.add(placeId);

    // Show a small non-blocking pill (not the full-screen loader) while fetching.
    setPoiResolving(true);
    createPlaceFromGoogle(placeId, Storage.getCurrentUserToken(), opts.sessionToken)
      .then((result) => {
        if (result.status === "success") {
          trackPoiPinSelected(result.venue);
          showResolvedVenue(result.venue, opts.center);
        } else {
          // For unsupported results from auto-suggest, the backend returns the
          // place coordinates so we can pan the map and anchor the message there.
          const resultLat = result.status === "unsupported" ? result.lat : null;
          const resultLng = result.status === "unsupported" ? result.lng : null;
          const lat = resultLat ?? opts.messageLat ?? mapRef?.getCenter()?.lat();
          const lng = resultLng ?? opts.messageLng ?? mapRef?.getCenter()?.lng();
          if (lat !== undefined && lng !== undefined && lat !== null && lng !== null) {
            if (opts.center && mapRef) {
              mapRef.panTo({ lat, lng });
            }
            setPoiMessage({
              lat,
              lng,
              text:
                result.status === "unsupported"
                  ? i18n.get("poiUnsupportedCategory")
                  : i18n.get("poiCouldNotAdd"),
              variant:
                result.status === "unsupported" ? "unsupported" : "error",
            });
          }
        }
      })
      .finally(() => {
        resolvingPlaceIds.current.delete(placeId);
        setPoiResolving(false);
      });
  };

  // A grey venue (in our DB but no accessibility data) looks like a not-in-DB
  // place to the user, so invite them to contribute instead of showing an empty
  // snippet. "Continue" opens the existing venue (see handleContributionContinue).
  const showContributionForVenue = (venue: Venue, center: boolean) => {
    // Only register a resolved pin if it isn't already on the map as a result pin.
    // Always register the venue so its pin survives the nearby reload this pick
    // triggers (which can drop it from `locations`). When it is in `locations`
    // the resolved-POI render dedupes it out, so there's no double pin.
    const greyVenue = { ...venue, venuePinType: VenuePinType.Grey };
    setResolvedPois((prev) =>
      prev.some(
        (v) =>
          (greyVenue.placeId && v.placeId === greyVenue.placeId) ||
          v.id === greyVenue.id,
      )
        ? prev
        : [...prev, greyVenue],
    );

    const openCard = () =>
      setPoiContribution({
        step: "intro",
        placeId: venue.placeId ?? "",
        lat: venue.latitude ?? mapRef?.getCenter()?.lat() ?? 0,
        lng: venue.longitude ?? mapRef?.getCenter()?.lng() ?? 0,
        name: venue.name,
        venue,
        selected: [],
        creating: false,
      });

    if (center && mapRef && venue.latitude != null && venue.longitude != null) {
      mapRef.panTo({ lat: venue.latitude, lng: venue.longitude });
      window.setTimeout(openCard, 350);
    } else {
      openCard();
    }
  };

  const isGreyVenue = (venue: Venue) =>
    (venue.reviewsDetailsSubmitted ?? 0) === 0;

  // All-places mode: classify a clicked POI without creating it. In-DB venues
  // with data show the snippet; grey (no-data) and supported-but-new venues open
  // the contribution card; unsupported categories show a terminal message.
  const handlePoiContribution = (
    placeId: string,
    fallbackLat?: number,
    fallbackLng?: number,
    opts?: {
      center?: boolean;
      sessionToken?: string;
      reloadNearby?: boolean;
      fallbackName?: string;
      // Only a real pin tap fires place_pin_selected — NOT autosuggest (selectGooglePlace).
      fromPinClick?: boolean;
    },
  ) => {
    const center = opts?.center ?? false;
    const reloadNearby = opts?.reloadNearby ?? false;

    // Recentre + reload the list nearby the resolved location. `search` stays
    // empty so the backend does a pure lat/lng search, not a text-name filter;
    // search_type "nearby" + simpleLocationText just drives the header label.
    const reloadListAroundCoords = (
      lat: number | null | undefined,
      lng: number | null | undefined,
      name?: string,
      pinnedVenueId?: string,
      pinnedVenueKind?: "grey" | "mapped",
    ) => {
      if (!reloadNearby || lat == null || lng == null) return;
      const latStr = lat.toString();
      const lngStr = lng.toString();
      suppressNextInitBoundsRef.current = true;
      setSearchPageParams((prev) => {
        // Same rule as "Search this area": every mode except "All places" is mapped-only.
        const currentMode = prev.map_mode ?? "sociability";
        // Grey (unmapped) venue → needs "All places"; a mapped (orange) venue
        // picked in "All places" → switch back to "Sociability Places".
        const switchToAll = pinnedVenueKind === "grey" && currentMode !== "all";
        const switchToSociability =
          pinnedVenueKind === "mapped" && currentMode === "all";
        const effectiveMode = switchToAll
          ? "all"
          : switchToSociability
            ? "sociability"
            : currentMode;
        return {
          ...prev,
          search: "",
          search_type: name ? "nearby" : undefined,
          search_value: undefined,
          simpleLocationText: name || "",
          searchThisAreaClicked: "no",
          autoZoom: "no",
          map_ne_lat: undefined,
          map_ne_lng: undefined,
          map_sw_lat: undefined,
          map_sw_lng: undefined,
          latitude: latStr,
          longitude: lngStr,
          unixTimestamp: Math.floor(Date.now() / 1000),
          ...(switchToAll
            ? { map_mode: "all" }
            : switchToSociability
              ? { map_mode: "sociability" }
              : {}),
          ...(enableGooglePoiClick
            ? { mapping_status: effectiveMode === "all" ? undefined : "mapped" }
            : {}),
        };
      });
      setPinnedFirstVenue(
        pinnedVenueId ? { id: pinnedVenueId, lat: latStr, lng: lngStr } : null,
      );
    };

    const reloadListAround = (venue: Venue, isGrey?: boolean) =>
      reloadListAroundCoords(
        venue.latitude,
        venue.longitude,
        venue.name || opts?.fallbackName,
        String(venue.id),
        isGrey ? "grey" : "mapped",
      );

    setPoiContribution(null);

    // About to become the focused venue; let the list card use it early instead
    // of waiting for the search response.
    const previewFocus = (venue: Venue) => {
      if (reloadNearby) setFocusPreview({ placeId, venue, state: "has_data" });
    };

    const existing = locations.find((l) => l.placeId && l.placeId === placeId);
    if (existing) {
      previewFocus(existing);
      reloadListAround(existing, isGreyVenue(existing));
      if (opts?.fromPinClick) trackPoiPinSelected(existing);
      // Grey pin (no accessibility data) → contribution card; otherwise snippet.
      if (isGreyVenue(existing)) {
        showContributionForVenue(existing, center);
      } else if (
        center &&
        mapRef &&
        existing.latitude != null &&
        existing.longitude != null
      ) {
        mapRef.panTo({ lat: existing.latitude, lng: existing.longitude });
        window.setTimeout(() => handleShowSnippet(existing.id), 350);
      } else {
        handleShowSnippet(existing.id);
      }
      return;
    }
    const alreadyResolved = resolvedPois.find((v) => v.placeId === placeId);
    if (alreadyResolved) {
      previewFocus(alreadyResolved);
      reloadListAround(alreadyResolved, isGreyVenue(alreadyResolved));
      if (opts?.fromPinClick) trackPoiPinSelected(alreadyResolved);
      if (isGreyVenue(alreadyResolved)) {
        showContributionForVenue(alreadyResolved, center);
      } else {
        showResolvedVenue(alreadyResolved, center);
      }
      return;
    }
    if (resolvingPlaceIds.current.has(placeId)) return;
    resolvingPlaceIds.current.add(placeId);

    setPoiResolving(true);
    createPlaceFromGoogle(
      placeId,
      Storage.getCurrentUserToken(),
      opts?.sessionToken,
      true,
    )
      .then((result) => {
        if (result.status === "success") {
          reloadListAround(result.venue, isGreyVenue(result.venue));
          if (opts?.fromPinClick) trackPoiPinSelected(result.venue);
          previewFocus(result.venue);
          // Already in our DB. Grey (no accessibility data) → invite the user to
          // contribute, same as a not-in-DB place. Otherwise behave like a pin.
          if (isGreyVenue(result.venue)) {
            showContributionForVenue(result.venue, center);
          } else {
            showResolvedVenue(result.venue, center);
          }
          return;
        }
        const lat = (result.status === "supported" || result.status === "unsupported"
          ? result.lat
          : null) ?? fallbackLat ?? mapRef?.getCenter()?.lat();
        const lng = (result.status === "supported" || result.status === "unsupported"
          ? result.lng
          : null) ?? fallbackLng ?? mapRef?.getCenter()?.lng();
        if (lat == null || lng == null) return;

        // Centre the POI so the anchored card (desktop) / bottom sheet (mobile)
        // stays fully on screen, mirroring the venue snippet's auto-pan.
        if (
          mapRef &&
          (result.status === "supported" || result.status === "unsupported")
        ) {
          mapRef.panTo({ lat, lng });
        }

        // No venue to pin, but still reload the list around the new location.
        if (result.status === "supported" || result.status === "unsupported") {
          const resultName = result.status === "supported" ? result.name : undefined;
          // "supported" = a real place not yet in our DB (unmapped/grey), so it
          // needs "All places"; "unsupported" can't be added → no mode change.
          reloadListAroundCoords(
            lat,
            lng,
            resultName || opts?.fallbackName,
            undefined,
            result.status === "supported" ? "grey" : undefined,
          );
        }

        if (result.status === "supported") {
          // Supported but not yet in our DB → no brand/business ids; it's a be_first_to_add place.
          if (opts?.fromPinClick)
            trackEvent("place_pin_selected", {
              brand_id: "",
              brand_name: "",
              business_id: "",
              entry_index: 0,
              pin_type: "be_first_to_add",
              place_name: result.name ?? "",
            });
          setPoiContribution({
            step: "intro",
            placeId,
            lat,
            lng,
            name: result.name,
            businessType: result.businessType,
            selected: [],
            creating: false,
          });
        } else if (result.status === "unsupported") {
          // Same "let the card use it early" idea, for the unsupported-category banner.
          // Enough of a venue shape for the map pin; the banner itself needs no fields.
          if (reloadNearby) {
            setFocusPreview({
              placeId,
              venue: {
                id: placeId,
                placeId,
                latitude: lat,
                longitude: lng,
                reviewsDetailsSubmitted: 0,
                business: { id: null, publicUrl: "" },
              } as unknown as Venue,
              state: "unsupported_category",
            });
          }
          setPoiContribution({
            step: "unsupported",
            placeId,
            lat,
            lng,
            category: result.category,
            selected: [],
            creating: false,
          });
        } else {
          setPoiMessage({ lat, lng, text: i18n.get("poiCouldNotAdd") });
        }
      })
      .finally(() => {
        resolvingPlaceIds.current.delete(placeId);
        setPoiResolving(false);
      });
  };

  // Shared brand/place dimensions for the POI contribution CTAs. `venue` is set only for grey pins
  // already in our DB; a raw Google POI has none yet -> "" + be_first_to_add (a no-data place).
  const poiCtaFields = () => ({
    brand_id:
      poiContribution?.venue?.brandId != null
        ? String(poiContribution.venue.brandId)
        : "",
    brand_name: poiContribution?.venue?.brandName ?? "",
    business_id: poiContribution?.venue?.business?.id?.toString() ?? "",
    pin_type: poiContribution?.venue
      ? pinTypeForVenue(poiContribution.venue)
      : "be_first_to_add",
    place_name: poiContribution?.name ?? "",
  });

  const handleContributionShowOptions = () => {
    // place_info_add_cta_activated — POI card "Add info" CTA (Google POI / grey venue).
    if (poiContribution)
      trackEvent("place_info_add_cta_activated", {
        ...poiCtaFields(),
        entry_page: entryPageFromCurrentPage(),
      });
    setPoiContribution((prev) => (prev ? { ...prev, step: "options" } : prev));
  };

  const handleContributionBack = () =>
    setPoiContribution((prev) => (prev ? { ...prev, step: "intro" } : prev));

  // Existing grey venue, or a freshly created grey pin from the POI. Null on failure.
  const resolveContributionVenue = async (): Promise<Venue | null> => {
    const current = poiContribution;
    if (!current) return null;
    if (current.venue) return current.venue;

    setPoiContribution((prev) => (prev ? { ...prev, creating: true } : prev));
    try {
      const result = await createPlaceFromGoogle(
        current.placeId,
        Storage.getCurrentUserToken(),
      );
      if (result.status !== "success") {
        setPoiContribution((prev) => (prev ? { ...prev, creating: false } : prev));
        setPoiMessage({
          lat: current.lat,
          lng: current.lng,
          text: i18n.get("poiCouldNotAdd"),
        });
        return null;
      }
      const hasAccessibilityData = hasVenueData(result.venue);
      const resolvedVenue = {
        ...result.venue,
        venuePinType: hasAccessibilityData
          ? resolveVenuePinType(result.venue)
          : VenuePinType.Grey,
      };
      setResolvedPois((prev) =>
        prev.some((v) => v.placeId === resolvedVenue.placeId)
          ? prev
          : [...prev, resolvedVenue],
      );
      return resolvedVenue;
    } catch {
      setPoiContribution((prev) => (prev ? { ...prev, creating: false } : prev));
      return null;
    }
  };

  // Deep-link to the venue detail page (login gated there); create venue if needed.
  const openVenueDetailForAction = async (search: string) => {
    if (!poiContribution || poiContribution.creating) return;
    const venue = await resolveContributionVenue();
    if (!venue) return;
    const slug =
      venue.business?.slug || venue.business?.publicUrl || String(venue.id);
    setPoiContribution(null);
    window.location.href = `/venuedetails/${slug}?${search}`;
  };

  // Resolve/create the grey venue, then open the host's notification popup.
  const openGetNotifiedInPlace = async () => {
    const venue = await resolveContributionVenue();
    if (!venue) return;
    setPoiContribution(null);
    props.onGetNotifiedForVenue?.(venue);
  };

  const handleContributionGetNotified = async () => {
    // place_notification_cta_activated — POI card "Get alerts" CTA. Fire on activation (before the
    // auth gate + both downstream paths), since neither the in-place modal nor the ?notify deep-link
    // fires it. Mirrors the sibling venue-card / complex-venue notification CTAs.
    if (poiContribution)
      trackEvent("place_notification_cta_activated", poiCtaFields());
    // Non-search hosts have no in-place popup — fall back to the venue-detail deep link.
    if (!props.onGetNotifiedForVenue) {
      openVenueDetailForAction("notify=true&source=map");
      return;
    }
    if (!poiContribution || poiContribution.creating) return;
    // Gate login; resume once logged in (see effect below).
    if (!isLoggedIn) {
      pendingGetNotifiedRef.current = true;
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    openGetNotifiedInPlace();
  };

  // Resume a pending "Get Alerts" after the user logs in.
  useEffect(() => {
    if (isLoggedIn && pendingGetNotifiedRef.current) {
      pendingGetNotifiedRef.current = false;
      openGetNotifiedInPlace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleContributionSelectAction = (
    action: "photos" | "experiences" | "alerts",
  ) => {
    const search =
      action === "experiences"
        ? "tab=reviews&openModal=true&source=map"
        : action === "alerts"
          ? // No "add alert" on desktop — reuse the "Get notified" flow instead.
            "notify=true&source=map"
          : "tab=photos&source=map";
    openVenueDetailForAction(search);
  };

  const handlePoiClick = (
    e: google.maps.MapMouseEvent & { placeId?: string; stop?: () => void },
  ): boolean => {
    if (!enableGooglePoiClick) return false;
    const placeId = e?.placeId;
    if (!placeId) return false;

    // Suppress Google's default POI info window.
    e.stop?.();

    handleCloseSnippet();
    handleClosePoi();

    resolvePlaceAndShow(placeId, {
      center: false,
      messageLat: e.latLng?.lat(),
      messageLng: e.latLng?.lng(),
    });

    return true;
  };

  // Determine where to show the snippet card relative to a pin.
  // Uses bounds-fraction interpolation to avoid large-number precision loss
  // that plagued the older `worldCoord * 2^zoom` approach.
  const getSnippetPosition = (location: Venue) => {
    const defaultWidth = 300;
    const defaultHeight = 400;
    const defaultPinHeight = 40;
    const fallback = {
      offset: { x: -(defaultWidth / 2), y: -defaultHeight - defaultPinHeight },
      className: "translate-x-[-50%] translate-y-[-105%]",
    };

    if (!mapRef) return fallback;

    try {
      const bounds = mapRef.getBounds();
      const projection = mapRef.getProjection();
      if (!bounds || !projection) return fallback;

      const mapDiv = mapRef.getDiv();
      const mapHeight = mapDiv.clientHeight;
      const mapWidth = mapDiv.clientWidth;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      // --- X: longitude is linear in Mercator ---
      let lngRange = ne.lng() - sw.lng();
      if (lngRange <= 0) lngRange += 360; // antimeridian wrap
      let pinLngOffset = location.longitude - sw.lng();
      if (pinLngOffset < 0) pinLngOffset += 360;
      const pinViewportX = (pinLngOffset / lngRange) * mapWidth;

      // --- Y: use world-coordinate fractions (stable, no large-number mult) ---
      const neWorld = projection.fromLatLngToPoint(ne);
      const swWorld = projection.fromLatLngToPoint(sw);
      const pinWorld = projection.fromLatLngToPoint(
        new google.maps.LatLng(location.latitude, location.longitude),
      );
      if (!neWorld || !swWorld || !pinWorld) return fallback;

      const worldHeightRange = swWorld.y - neWorld.y; // positive: south > north in world Y
      const pinViewportY =
        worldHeightRange > 0
          ? ((pinWorld.y - neWorld.y) / worldHeightRange) * mapHeight
          : mapHeight / 2;

      // Snippet actual dimensions
      const actualSnippetHeight = 400; // Actual height of the VenueCard snippet
      const actualSnippetWidth = 300; // Actual width of the VenueCard snippet

      // Snippet dimensions for space calculations (with buffer for shadows, borders, and safety)
      const snippetHeight = 440; // Height + extra buffer for shadows and top safety
      const snippetWidth = 320; // Width + extra buffer for shadows
      const pinHeight = 40; // Height of the pin marker
      const marginHorizontal = 50; // Increased safety margin from left/right edges
      const marginTop = 80; // Extra margin for top edge (more critical due to browser chrome)
      const marginBottom = 50; // Standard margin for bottom edge

      // Calculate available space on all sides
      const spaceAbove = pinViewportY - marginTop;
      const spaceBelow = mapHeight - pinViewportY - pinHeight - marginBottom;
      const spaceLeft = pinViewportX - marginHorizontal;
      const spaceRight = mapWidth - pinViewportX - marginHorizontal;

      // Determine if snippet fits in each direction with buffer
      // For top positioning, add extra 30px buffer to be more conservative
      const fitsAbove = spaceAbove >= snippetHeight + 30;
      const fitsBelow = spaceBelow >= snippetHeight;

      // For horizontal positioning, check if there's space for the full snippet width
      // when centered (need half width on each side) - with extra buffer
      const fitsCentered =
        spaceLeft >= snippetWidth / 2 + 10 &&
        spaceRight >= snippetWidth / 2 + 10;

      // CheckIcon if full snippet can fit to the right (when aligned left from pin) - with extra buffer
      const fitsFullyRight = spaceRight >= snippetWidth + 10;

      // CheckIcon if full snippet can fit to the left (when aligned right from pin) - with extra buffer
      const fitsFullyLeft = spaceLeft >= snippetWidth + 10;

      // Determine vertical position (above or below) - use actual dimensions for offset
      // Add extra offset padding for top positioning to prevent cutoff
      let yOffset = -actualSnippetHeight - pinHeight - 10; // Extra 10px padding when above
      let translateY = "translate-y-[-105%]";

      if (fitsAbove && !fitsBelow) {
        yOffset = -actualSnippetHeight - pinHeight - 10; // Extra padding
        translateY = "translate-y-[-105%]";
      } else if (fitsBelow && !fitsAbove) {
        yOffset = pinHeight + 5; // Small padding below too
        translateY = "translate-y-[5%]";
      } else if (!fitsAbove && !fitsBelow) {
        // Neither fits perfectly, strongly prefer below to avoid top cutoff
        if (spaceBelow > spaceAbove - 50) {
          // Bias towards below by 50px
          yOffset = pinHeight + 5;
          translateY = "translate-y-[5%]";
        } else {
          yOffset = -actualSnippetHeight - pinHeight - 10;
          translateY = "translate-y-[-105%]";
        }
      } else {
        // Both fit, but prefer below if pin is in top 40% of screen to avoid top edge
        const pinInTopPortion = pinViewportY < mapHeight * 0.4;
        if (pinInTopPortion) {
          // Pin is near top, prefer showing below
          yOffset = pinHeight + 5;
          translateY = "translate-y-[5%]";
        } else {
          // Pin has plenty of space above, show above
          yOffset = -actualSnippetHeight - pinHeight - 10;
          translateY = "translate-y-[-105%]";
        }
      }

      // Determine horizontal position (center, left, or right aligned) - use actual dimensions for offset
      let xOffset = -(actualSnippetWidth / 2); // default: centered
      let translateX = "translate-x-[-50%]";

      if (fitsCentered) {
        // Snippet fits centered, keep default
        xOffset = -(actualSnippetWidth / 2);
        translateX = "translate-x-[-50%]";
      } else if (!fitsFullyRight && fitsFullyLeft) {
        // Too close to right edge, align snippet to the LEFT of the pin
        xOffset = -actualSnippetWidth;
        translateX = "translate-x-[-100%]";
      } else if (!fitsFullyLeft && fitsFullyRight) {
        // Too close to left edge, align snippet to the RIGHT of the pin
        xOffset = 0;
        translateX = "translate-x-[0%]";
      } else if (!fitsFullyLeft && !fitsFullyRight) {
        // Doesn't fit on either side fully, choose side with more space
        if (spaceRight > spaceLeft) {
          xOffset = 0;
          translateX = "translate-x-[0%]";
        } else {
          xOffset = -actualSnippetWidth;
          translateX = "translate-x-[-100%]";
        }
      } else {
        // Both sides fit, prefer centered (already set as default)
        xOffset = -(actualSnippetWidth / 2);
        translateX = "translate-x-[-50%]";
      }

      return {
        offset: { x: xOffset, y: yOffset },
        className: `${translateX} ${translateY}`,
      };
    } catch (error) {
      console.error("Error calculating snippet position:", error);
      return fallback;
    }
  };

  const {
    location: userLocation,
    loading: locationLoading,
    error: locationError,
    requestUserLocation,
  } = useGeoLocation();

  // Result of search api
  // Note: We always call useHomeVenues() (React hooks rules), but when locationsOverride
  // is provided (e.g., from search page), we use that instead to avoid redundant data usage
  const {
    locations: locationsFromHook = [],
    isLoading,
    isFetching,
    data: venuesData,
  } = useHomeVenues();

  // Reloading with pins still on screen (keepPreviousData) — inline spinner only.
  const isBackgroundReloading = isFetching && !isLoading;

  // Use override locations if provided (search page passes filtered locations),
  // otherwise use locations from hook (saved places/venue details pages fetch their own data)
  const locationsBase = locationsOverride || locationsFromHook;

  // Venue-detail pins come from a nearby search that may omit the current venue —
  // inject it so it always renders/highlights as selected.
  const locations = useMemo(() => {
    if (props.mapSource !== "venuedetails" || !selectedVenue) return locationsBase;
    if (locationsBase.some((l) => l.id === selectedVenue.id)) return locationsBase;
    return [
      ...locationsBase,
      {
        ...selectedVenue,
        venuePinType:
          selectedVenue.venuePinType ?? resolveVenuePinType(selectedVenue),
      },
    ];
  }, [locationsBase, selectedVenue, props.mapSource]);

  // Clear resolved POI pins whenever the underlying search results change
  // (new search / show nearby / re-search), so they don't linger across searches.
  // Keep the venue the user just picked from autosuggest (pinned) or currently
  // has open — its own pick triggers a nearby reload, and we don't want that to
  // wipe the highlighted pin the card is anchored to.
  useEffect(() => {
    setResolvedPois((prev) => {
      if (!prev.length) return prev;
      const keep = prev.filter(
        (v) =>
          pinnedFirstVenue?.id === v.id ||
          activeMarker === v.id ||
          selectedVenue?.id === v.id,
      );
      return keep.length === prev.length ? prev : keep;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Stable snippet venue + position — memoised on activeMarker so that unrelated
  // state changes (e.g. hoveredVenueId from VenueCard) do not produce new object
  // references, which would cause vis.gl to call infoWindow.setPosition() and
  // trigger a visible flicker on the Advanced Markers path.
  const snippetVenueAdv = useMemo(() => {
    if (!activeMarker) return null;
    return (
      locations.find((l) => l.id === activeMarker) ||
      resolvedPois.find((v) => v.id === activeMarker) ||
      null
    );
  }, [activeMarker, locations, resolvedPois]);

  const snippetPositionAdv = useMemo(
    () =>
      snippetVenueAdv
        ? { lat: snippetVenueAdv.latitude, lng: snippetVenueAdv.longitude }
        : null,
    [snippetVenueAdv],
  );

  // Pan the map to new search coordinates dispatched by performSearch.
  // This ensures the map follows an explicit location search even when
  // enableAutoZoom is disabled.
  useEffect(() => {
    const handlePanMapToCenter = (
      event: CustomEvent<{ lat: number; lng: number }>,
    ) => {
      const { lat, lng } = event.detail ?? {};
      if (lat == null || lng == null || !mapRef) return;
      mapRef.panTo({ lat, lng });
    };
    window.addEventListener(
      "panMapToCenter",
      handlePanMapToCenter as EventListener,
    );
    return () => {
      window.removeEventListener(
        "panMapToCenter",
        handlePanMapToCenter as EventListener,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);

  // Resolve a Google place chosen from the search-bar auto-suggest: center the map,
  // drop a pin and show the preview (or an unsupported/error message).
  useEffect(() => {
    const handleSelectGooglePlace = (
      event: CustomEvent<{
        placeId: string;
        sessionToken?: string;
        name?: string;
        requestId?: string;
      }>,
    ) => {
      const placeId = event.detail?.placeId;
      if (!placeId || !enableGooglePoiClick) return;
      // Only the on-screen map instance should handle this. The component mounts
      // twice (desktop + mobile); the hidden one must not swallow the event and
      // resolve it on its off-screen map (offsetParent is null when display:none).
      const mapDiv = mapRef?.getDiv() as HTMLElement | null | undefined;
      if (!mapDiv || mapDiv.offsetParent === null) return;
      // Dedupe across the two mounted instances (see var comment above).
      const requestId = event.detail?.requestId ?? placeId;
      const now = Date.now();
      if (
        requestId === lastHandledSelectGooglePlaceRequestId &&
        now - lastHandledSelectGooglePlaceAt < SELECT_GOOGLE_PLACE_DEDUPE_WINDOW_MS
      ) {
        return;
      }
      lastHandledSelectGooglePlaceRequestId = requestId;
      lastHandledSelectGooglePlaceAt = now;
      handleCloseSnippet();
      handleClosePoi();
      setLocationSearchAnchor(null);
      // Lookup-first: never silently create. In-DB → snippet, supported-but-new →
      // opt-in contribution card, unsupported type → terminal message card.
      handlePoiContribution(placeId, undefined, undefined, {
        center: true,
        sessionToken: event.detail?.sessionToken,
        reloadNearby: true,
        fallbackName: event.detail?.name,
      });
    };
    window.addEventListener(
      "selectGooglePlace",
      handleSelectGooglePlace as EventListener,
    );
    return () => {
      window.removeEventListener(
        "selectGooglePlace",
        handleSelectGooglePlace as EventListener,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, locations, resolvedPois]);

  // When arriving from the home page with a pending Google place (stored in
  // pendingGooglePlaceIdAtom), resolve it once the map is ready.
  useEffect(() => {
    if (!pendingGooglePlaceId || !mapRef || !enableGooglePoiClick) return;
    const placeId = pendingGooglePlaceId;
    setPendingGooglePlaceId(""); // clear immediately to prevent re-triggering
    window.dispatchEvent(
      new CustomEvent("selectGooglePlace", {
        detail: {
          placeId,
          requestId: `${placeId}:${Date.now()}:${Math.random()
            .toString(36)
            .slice(2)}`,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGooglePlaceId, mapRef]);

  // Area / station / airport and compound selections no longer geocode here:
  // SearchBar's action dispatcher resolves coordinates server-side (closing the
  // session token) and writes them into searchPageParams, so the existing
  // search-params effect centres the map and runs the nearby search. This removed
  // the pendingGoogleAreaId / pendingCompoundSearch atoms and the
  // selectGoogleArea / compoundAreaSearch custom-event dance.

  const { setSearchPageParams, searchPageParams } = useSearchPageParams();

  // A searched-for venue keeps a highlighted pin until the focus ends.
  const focusPlaceIds = useMemo(
    () => new Set(parseFocusPlaceIds(searchPageParams.focus_place_ids)),
    [searchPageParams.focus_place_ids],
  );
  const isFocusedPlace = useCallback(
    (venue: Venue) => !!venue.placeId && focusPlaceIds.has(venue.placeId),
    [focusPlaceIds],
  );

  // Clicking another pin retires the searched venue's highlighted pin (it stays
  // on the map as a default pin, and stays focused in the list) until a new search.
  const [focusPinRetired, setFocusPinRetired] = useState(false);
  useEffect(() => {
    setFocusPinRetired(false);
  }, [focusPlaceIds]);

  // A new search ends the focus, so the pin and snippet it left behind go too.
  const hadFocusRef = useRef(false);
  useEffect(() => {
    const hasFocus = focusPlaceIds.size > 0;
    const lostFocus = hadFocusRef.current && !hasFocus;
    hadFocusRef.current = hasFocus;
    if (!lostFocus) return;

    setResolvedPois([]);
    setPinnedFirstVenue(null);
    setPoiMessage(null);
    setActiveMarker(null);
    setSnippetPosition(null);
    setTooltipVenue(null);
    setSnippetOpen(false);
    setFocusPreview(null);
  }, [focusPlaceIds, setPinnedFirstVenue, setFocusPreview]);

  // A raw map POI tap never sets focus_place_ids, so it can outlive an unrelated
  // new search. Deliberate searches flag autoZoom="yes" (nearby reloads use "no").
  const lastClearedSearchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (searchPageParams.autoZoom !== "yes") return;
    const key = [
      searchPageParams.search,
      searchPageParams.search_type,
      searchPageParams.search_value,
      searchPageParams.latitude,
      searchPageParams.longitude,
    ].join("|");
    if (key === lastClearedSearchKeyRef.current) return;
    lastClearedSearchKeyRef.current = key;

    setResolvedPois([]);
    setPoiMessage(null);
    setPoiContribution(null);
    setActiveMarker(null);
    setSnippetPosition(null);
    setTooltipVenue(null);
    setSnippetOpen(false);
    setFocusPreview(null);
  }, [
    searchPageParams.autoZoom,
    searchPageParams.search,
    searchPageParams.search_type,
    searchPageParams.search_value,
    searchPageParams.latitude,
    searchPageParams.longitude,
    setFocusPreview,
  ]);

  // One pick at a time: an area marker dismisses a venue card, and opening a card
  // clears the marker (handleShowSnippet / showResolvedVenue).
  useEffect(() => {
    if (!locationSearchAnchor) return;
    handleCloseSnippet();
    handleClosePoi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearchAnchor]);

  const { mapMode, changeMapMode } = useMapMode();

  // Area / station / airport / compound selections arrive via searchPageParams
  // (SearchBar resolves the coords server-side). An already-loaded map doesn't
  // recenter from the `center` prop, so explicitly pan to the area when one of
  // these "search this area" selections is applied. Guarded by unixTimestamp so
  // we pan once per selection, not on unrelated re-renders.
  const lastPannedAreaTsRef = useRef<number | null>(null);
  useEffect(() => {
    if (!mapRef || searchPageParams.searchThisAreaClicked !== "yes") return;
    const ts = Number(searchPageParams.unixTimestamp) || 0;
    if (ts === lastPannedAreaTsRef.current) return;
    const lat = parseFloat(searchPageParams.latitude);
    const lng = parseFloat(searchPageParams.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    lastPannedAreaTsRef.current = ts;
    mapRef.panTo({ lat, lng });
    setMapCenter({ lat, lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapRef,
    searchPageParams.searchThisAreaClicked,
    searchPageParams.latitude,
    searchPageParams.longitude,
    searchPageParams.unixTimestamp,
  ]);

  // A deliberate keyword / compound / category search (performSearch) writes the
  // new search centre into searchPageParams and flags autoZoom="yes". An
  // already-mounted map ignores the `center` prop, so pan it to the new centre.
  // Progressive map-drag reloads and manual "search this area" set autoZoom="no",
  // so they never hit this. Deduped by coordinate value to avoid fighting drags.
  const lastPannedSearchCenterRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mapRef || searchPageParams.autoZoom !== "yes") return;
    const lat = parseFloat(searchPageParams.latitude);
    const lng = parseFloat(searchPageParams.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    const key = `${lat},${lng}`;
    if (key === lastPannedSearchCenterRef.current) return;
    lastPannedSearchCenterRef.current = key;
    mapRef.panTo({ lat, lng });
    setMapCenter({ lat, lng });
    // Snap out of a stale world zoom carried over across navigation.
    const z = mapRef.getZoom();
    if (typeof z === "number" && z < 8) {
      const parsed = parseInt(searchPageParams.zoom ?? "", 10);
      const target = parsed >= 11 && parsed <= 16 ? parsed : 14;
      mapRef.setZoom(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapRef,
    searchPageParams.autoZoom,
    searchPageParams.latitude,
    searchPageParams.longitude,
  ]);

  useEffect(() => {
    // Skip this effect on the /search route
    if (isSearchRoute && !goToUserLocationRequestedRef.current) {
      return;
    }

    // console.log("goToUserLocationRequestedRef", goToUserLocationRequestedRef.current);

    if (userLocation && mapRef) {
      // UserIcon explicitly clicked "Go to current location" ï¿½ force full reset of search center
      if (goToUserLocationRequestedRef.current) {
        mapRef.panTo({
          lat: userLocation.latitude,
          lng: userLocation.longitude,
        });
        setMapCenter({
          lat: userLocation.latitude,
          lng: userLocation.longitude,
        });
        setSearchPageParams((prev) => ({
          ...prev,
          userlatitude: userLocation.latitude.toString(),
          userlongitude: userLocation.longitude.toString(),
          latitude: userLocation.latitude.toString(),
          longitude: userLocation.longitude.toString(),
          zoom: mapRef?.getZoom()?.toString() || "16",
          saved_place_id: savedPlaceIdToView,
          saved_place_user_id: savedPlaceByUserIdToView,
          saved_place_sortby: savedPlaceSortByToView,
          unixTimestamp: Math.floor(Date.now() / 1000),
        }));
        goToUserLocationRequestedRef.current = false;
        return;
      }

      setSearchPageParams((prev) => {
        // CheckIcon if latitude and longitude are already set (e.g., from shared search or custom location)
        const hasExistingCoordinates =
          prev.latitude !== null &&
          prev.longitude !== null &&
          prev.latitude !== undefined &&
          prev.longitude !== undefined;

        if (savedPlaceIdToView == "") {
          if (hasExistingCoordinates) {
            // Pan to the existing custom coordinates (shared from home page)
            const safeLat =
              parseFloat(prev.latitude || "") || defaultRegion.latitude;
            const safeLng =
              parseFloat(prev.longitude || "") || defaultRegion.longitude;
            mapRef.panTo({
              lat: safeLat,
              lng: safeLng,
            });
          } else {
            // No existing coordinates, use user's geolocation
            mapRef.panTo({
              lat: userLocation.latitude,
              lng: userLocation.longitude,
            });
          }
        }

        // If coordinates already exist AND this is the initial load, skip updating userlatitude/userlongitude
        // to prevent triggering an extra API call when navigating from home page with search params
        if (hasExistingCoordinates && !hasSetInitialUserLocationRef.current) {
          console.log(
            "[BasicMapView] Skipping initial userLocation update to prevent duplicate API call",
          );
          hasSetInitialUserLocationRef.current = true;
          return prev; // Return unchanged to avoid triggering API call
        }

        // If coordinates already exist, only update user location fields (not search center)
        if (hasExistingCoordinates) {
          return {
            ...prev,
            userlatitude: userLocation.latitude.toString(),
            userlongitude: userLocation.longitude.toString(),
            // DO NOT update latitude/longitude - preserve the custom location
            zoom: mapRef?.getZoom()?.toString() || "16",
            saved_place_id: savedPlaceIdToView,
            saved_place_user_id: savedPlaceByUserIdToView,
            saved_place_sortby: savedPlaceSortByToView,
            // Don't update unixTimestamp to avoid unnecessary API calls
          };
        }

        // No existing coordinates - set everything including search center
        hasSetInitialUserLocationRef.current = true;
        return {
          ...prev,
          userlatitude: userLocation.latitude.toString(),
          userlongitude: userLocation.longitude.toString(),
          latitude: userLocation.latitude.toString(),
          longitude: userLocation.longitude.toString(),
          zoom: mapRef?.getZoom()?.toString() || "16",
          saved_place_id: savedPlaceIdToView,
          saved_place_user_id: savedPlaceByUserIdToView,
          saved_place_sortby: savedPlaceSortByToView,
          unixTimestamp: Math.floor(Date.now() / 1000),
        };
      });
    }
  }, [
    userLocation,
    mapRef,
    setSearchPageParams,
    setMapCenter,
    savedPlaceIdToView,
    savedPlaceByUserIdToView,
    savedPlaceSortByToView,
  ]);

  useEffect(() => {
    // Skip this effect on the /search route
    if (isSearchRoute) {
      return;
    }

    if (locationError) {
      if (mapRef) {
        console.log(
          "Setting default location and loading pins savedPlaceIdToView",
          savedPlaceIdToView,
          "savedPlaceByUserIdToView",
          savedPlaceByUserIdToView,
        );
        setSearchPageParams((prev) => {
          // CheckIcon if latitude and longitude are already set (e.g., from shared search or custom location)
          const hasExistingCoordinates =
            prev.latitude !== null &&
            prev.longitude !== null &&
            prev.latitude !== undefined &&
            prev.longitude !== undefined;

          if (savedPlaceIdToView == "") {
            if (hasExistingCoordinates) {
              // Pan to the existing custom coordinates (shared from home page)
              const safeLat =
                parseFloat(prev.latitude || "") || defaultRegion.latitude;
              const safeLng =
                parseFloat(prev.longitude || "") || defaultRegion.longitude;
              mapRef.panTo({
                lat: safeLat,
                lng: safeLng,
              });
            } else {
              // No existing coordinates, use default region
              mapRef.panTo({
                lat: defaultRegion.latitude,
                lng: defaultRegion.longitude,
              });
            }
          }

          // If coordinates already exist AND this is the initial load, skip updating userlatitude/userlongitude
          if (hasExistingCoordinates && !hasSetInitialUserLocationRef.current) {
            console.log(
              "[BasicMapView] Skipping initial location error fallback update to prevent duplicate API call",
            );
            hasSetInitialUserLocationRef.current = true;
            return prev; // Return unchanged to avoid triggering API call
          }

          // If coordinates already exist, only update user location fields (not search center)
          if (hasExistingCoordinates) {
            return {
              ...prev,
              userlatitude: defaultRegion.latitude.toString(),
              userlongitude: defaultRegion.longitude.toString(),
              // DO NOT update latitude/longitude - preserve the custom location
              zoom: mapRef?.getZoom()?.toString() || "16",
              saved_place_id: savedPlaceIdToView,
              saved_place_user_id: savedPlaceByUserIdToView,
              saved_place_sortby: savedPlaceSortByToView,
              // Don't update unixTimestamp to avoid unnecessary API calls
            };
          }

          // No existing coordinates - set everything including search center
          hasSetInitialUserLocationRef.current = true;
          return {
            ...prev,
            userlatitude: defaultRegion.latitude.toString(),
            userlongitude: defaultRegion.longitude.toString(),
            latitude: defaultRegion.latitude.toString(),
            longitude: defaultRegion.longitude.toString(),
            zoom: mapRef?.getZoom()?.toString() || "16",
            saved_place_id: savedPlaceIdToView,
            saved_place_user_id: savedPlaceByUserIdToView,
            saved_place_sortby: savedPlaceSortByToView,
            unixTimestamp: Math.floor(Date.now() / 1000),
          };
        });
      }
    }
  }, [
    isSearchRoute,
    locationError,
    mapRef,
    savedPlaceByUserIdToView,
    savedPlaceIdToView,
    savedPlaceSortByToView,
    setSearchPageParams,
    setMapCenter,
  ]);

  // Viewport-box params from map bounds. An over-large span (e.g. world view)
  // is dropped so the backend uses the radius instead of fetching globally.
  const MAX_VIEWPORT_SPAN_DEG = 30;
  const viewportBoxParams = (
    ne?: google.maps.LatLng | null,
    sw?: google.maps.LatLng | null,
  ) => {
    const cleared = {
      map_ne_lat: undefined,
      map_ne_lng: undefined,
      map_sw_lat: undefined,
      map_sw_lng: undefined,
    };
    if (!ne || !sw) return cleared;
    const latSpan = Math.abs(ne.lat() - sw.lat());
    const lngSpan = Math.abs(ne.lng() - sw.lng());
    if (latSpan > MAX_VIEWPORT_SPAN_DEG || lngSpan > MAX_VIEWPORT_SPAN_DEG) {
      return cleared;
    }
    return {
      map_ne_lat: ne.lat().toString(),
      map_ne_lng: ne.lng().toString(),
      map_sw_lat: sw.lat().toString(),
      map_sw_lng: sw.lng().toString(),
    };
  };

  const onMapMoved = () => {
    // Always publish the live viewport (centre + bounds) so the map-mode toggle
    // can search the current view without holding a map ref.
    const vpCenter = mapRef?.getCenter();
    const vpBounds = mapRef?.getBounds();
    if (vpCenter && vpBounds) {
      const ne = vpBounds.getNorthEast();
      const sw = vpBounds.getSouthWest();
      setMapViewport({
        lat: vpCenter.lat(),
        lng: vpCenter.lng(),
        ne: { lat: ne.lat(), lng: ne.lng() },
        sw: { lat: sw.lat(), lng: sw.lng() },
        zoom: mapRef?.getZoom() ?? 14,
      });
    }
    const vpZoom = mapRef?.getZoom();
    if (typeof vpZoom === "number") setMapZoom(vpZoom);

    // Skip if map hasn't completed initial load yet
    // This prevents overwriting synced location params when the idle event fires on initial map load
    if (!mapHasCompletedInitialLoadRef.current) {
      console.log("Skipping onMapMoved - map still initializing");
      // Mark as completed and store the current params
      // This ensures we only respond to REAL user movements, not initial map setup
      mapHasCompletedInitialLoadRef.current = true;
      lastInitializedParamsRef.current = {
        lat: searchPageParams.latitude,
        lng: searchPageParams.longitude,
      };
      // Seed zoom baseline so the first user zoom is compared against the real initial zoom
      lastReloadedZoomRef.current = mapRef?.getZoom() ?? null;
      console.log(
        "Map initialization complete - onMapMoved now enabled for params:",
        lastInitializedParamsRef.current,
      );

      // On the search page in Sociability ("Places with data") mode, send the
      // initial viewport bounds by default so the first load filters to the view.
      // Only when the POI-click feature (map-mode toggle) is enabled. Skipped
      // right after a deliberate "reload nearby" (auto-suggest pick), which
      // already cleared bounds on purpose for a wider, unfiltered search.

      // If the map settled at a world/near-zero zoom (happens in "all" mode where
      // the init-bounds path is skipped), force it onto the search centre at a
      // sane zoom so it doesn't sit at the whole-world view.
      const initZoom = mapRef?.getZoom();
      const initLat = parseFloat(searchPageParams.latitude ?? "");
      const initLng = parseFloat(searchPageParams.longitude ?? "");
      if (
        isSearchRoute &&
        (initZoom == null || initZoom <= 2) &&
        !isNaN(initLat) &&
        !isNaN(initLng) &&
        (initLat !== 0 || initLng !== 0)
      ) {
        const parsedZoom = parseInt(searchPageParams.zoom ?? "", 10);
        const targetZoom = parsedZoom >= 11 && parsedZoom <= 16 ? parsedZoom : 14;
        mapRef.setCenter({ lat: initLat, lng: initLng });
        mapRef.setZoom(targetZoom);
        lastReloadedZoomRef.current = targetZoom;
      }

      if (suppressNextInitBoundsRef.current) {
        suppressNextInitBoundsRef.current = false;
      } else if (
        isSearchRoute &&
        enableGooglePoiClick &&
        searchPageParams.map_mode !== "all" &&
        !searchPageParams.map_ne_lat
      ) {
        const initBounds = mapRef?.getBounds();
        const initNe = initBounds?.getNorthEast();
        const initSw = initBounds?.getSouthWest();
        // Mapped-only initial load for every mode except "declutter" and "all".
        const initIsSociability = searchPageParams.map_mode !== "declutter";
        if (initNe && initSw) {
          setSearchPageParams((prev) => ({
            ...prev,
            searchThisAreaClicked: "no",
            autoZoom: "no",
            ...viewportBoxParams(initNe, initSw),
            unixTimestamp: Math.floor(Date.now() / 1000),
            ...(initIsSociability ? { mapping_status: "mapped" } : {}),
          }));
        }
      }
      return;
    }

    // Additional check: If searchPageParams have changed since we initialized,
    // skip this event (it's from the previous location's map settling)
    if (
      lastInitializedParamsRef.current.lat !== searchPageParams.latitude ||
      lastInitializedParamsRef.current.lng !== searchPageParams.longitude
    ) {
      console.log(
        "Skipping onMapMoved - searchPageParams changed since map initialization",
      );
      // Update to new params and wait for next idle
      lastInitializedParamsRef.current = {
        lat: searchPageParams.latitude,
        lng: searchPageParams.longitude,
      };
      return;
    }

    // "Places with data" + "Overview" (clustered/dots) stay mapped-only on reload.
    const isSociabilityCluster =
      isSearchRoute &&
      searchPageParams.map_mode !== "all" &&
      searchPageParams.map_mode !== "declutter";
    // Auto-load (zoom + pan) is eligible on the search route in any mode except
    // "declutter" (which relies on collision decluttering, not reload).
    const autoloadEligible =
      isSearchRoute && searchPageParams.map_mode !== "declutter";

    // mapUpdate is the single master switch for all auto-load (zoom and pan).
    if (!mapSettings?.mapUpdate || !autoloadEligible) {
      return;
    }

    if (savedPlaceIdToView !== "") {
      console.log("No progresive search on View saved places");
      return;
    }

    // An open snippet no longer blocks the reload: it survives a pan, so the
    // results underneath it still need to follow the map.

    // Debounce: cancel any pending reload and wait for the user to settle
    // before firing — handles rapid zoom steps and mid-pan idle events
    const debounceMs = parseInt(mapSettings?.mapUpdateTimeout || "1000", 10);
    if (progressiveSearchTimerRef.current) {
      clearTimeout(progressiveSearchTimerRef.current);
    }

    progressiveSearchTimerRef.current = setTimeout(() => {
      progressiveSearchTimerRef.current = null;

      const newCenter = mapRef?.getCenter();
      const currentZoom = mapRef?.getZoom() ?? null;

      // Guard against an uninitialized / world-view map (zoom 0-2, or a centre
      // parked on null-island). Reloading here would overwrite the real search
      // lat/lng with (0,0) and strand the map at the whole-world view.
      if (
        !newCenter ||
        currentZoom == null ||
        currentZoom <= 2 ||
        (Math.abs(newCenter.lat()) < 0.001 && Math.abs(newCenter.lng()) < 0.001)
      ) {
        return;
      }

      const prevCenter = new google.maps.LatLng(
        parseFloat(searchPageParams.latitude),
        parseFloat(searchPageParams.longitude),
      );

      const distanceInMeters =
        google.maps.geometry.spherical.computeDistanceBetween(
          prevCenter,
          newCenter,
        );

      // Scale the distance threshold by zoom level — the base distance (from settings)
      // applies at zoom 14. Each zoom level in halves the threshold (map viewport shrinks),
      // each level out doubles it. Clamped between 50m and 5000m.
      const BASE_ZOOM = 14;
      const baseDistanceMeters = parseFloat(mapSettings?.mapUpdateDistance || "0.5") * 1000;
      const zoomForThreshold = currentZoom ?? BASE_ZOOM;
      const dynamicThresholdMeters = Math.min(
        5000,
        Math.max(50, baseDistanceMeters * Math.pow(2, BASE_ZOOM - zoomForThreshold)),
      );

      const movedBeyondThreshold = distanceInMeters > dynamicThresholdMeters;

      // Require at least 1 zoom level of change since the last reload
      // (debounce above already handles rapid stepping — this just prevents
      //  spurious re-fires when idle fires without a real zoom change)
      const ZOOM_LEVEL_THRESHOLD = 1;
      const zoomDelta =
        lastReloadedZoomRef.current !== null && currentZoom !== null
          ? Math.abs(currentZoom - lastReloadedZoomRef.current)
          : 0;
      // Zoom autoload: any >=1 level change (in or out) refetches for the new view.
      const zoomAutoload = zoomDelta >= ZOOM_LEVEL_THRESHOLD;

      // Pan autoload is desktop-only (Tailwind lg breakpoint = 1024px).
      const isDesktop =
        typeof window !== "undefined" &&
        window.matchMedia("(min-width: 1024px)").matches;
      const panAutoload = isDesktop && movedBeyondThreshold;

      if (zoomAutoload || panAutoload) {
        console.log(
          zoomAutoload
            ? `Progressive search: zoom autoload (${lastReloadedZoomRef.current} -> ${currentZoom})`
            : `Progressive search: reloading on map move (${distanceInMeters.toFixed(0)}m, threshold ${dynamicThresholdMeters.toFixed(0)}m at zoom ${zoomForThreshold})`,
          newCenter?.lat().toString(),
          newCenter?.lng().toString(),
        );
        // Update last-reloaded zoom so future deltas are measured from here
        lastReloadedZoomRef.current = currentZoom;
        // Mark that the user moved the map to suppress auto-zoom briefly
        userMovedMapAtRef.current = Date.now();

        // Capture the visible viewport bounds so the backend can filter
        // to exactly what the user can see (replaces radius circle at zoom >= 14)
        const bounds = mapRef?.getBounds();
        const ne = bounds?.getNorthEast();
        const sw = bounds?.getSouthWest();

        // Pure zoom-in (no pan): keep lat/lng untouched so the "location changed"
        // reset effect doesn't fire and re-seed the zoom baseline (which would
        // stop the autoload from re-triggering). Only rewrite them on a real pan.
        const centerMoved = movedBeyondThreshold;

        setSearchPageParams((prev) => {
          const params = {
            ...prev,
            ...(centerMoved
              ? {
                  latitude: newCenter?.lat().toString() || "",
                  longitude: newCenter?.lng().toString() || "",
                }
              : {}),
            zoom: currentZoom?.toString() || "14",
            searchThisAreaClicked: "no",
            autoZoom: "no", // progressive reload must never trigger auto-zoom
            unixTimestamp: Math.floor(Date.now() / 1000),
            includeGoogleFallback: "false",
            // "Places with data" must keep filtering to mapped venues on reload.
            ...(isSociabilityCluster ? { mapping_status: "mapped" } : {}),
            ...viewportBoxParams(ne, sw),
          };
          // Clear expand_radius specifically — other search types (brand, venue_name, etc.) should persist
          if (prev.search_type === "expand_radius") {
            delete params.search_type;
            delete params.search_value;
          }
          return params;
        });

        // On a pan, keep the init baseline in sync with our own reload so the
        // next idle isn't discarded by the "params changed" guard above.
        if (centerMoved) {
          lastInitializedParamsRef.current = {
            lat: newCenter?.lat().toString() || "",
            lng: newCenter?.lng().toString() || "",
          };
        }
      }
    }, debounceMs);
  };

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    setMapRef(map);
  }, []);

  const onUnmount = React.useCallback(function callback() {
    if (progressiveSearchTimerRef.current) {
      clearTimeout(progressiveSearchTimerRef.current);
    }
    setMapRef(null);
  }, []);

  const getPinIcon = (venue: Venue) => {
    // console.log("venue.venuePinType isMapperViewActive ", isMapperViewActive, venue.venuePinType);
    // User view: Orange/Grey only, keyed off hasVenueData not venuePinType
    // (a non-Grey pinType like Purple can still mean zero real data).
    if (!isMapperViewActive) {
      return hasVenueData(venue) ? OrangePin : GreyPin;
    }

    // Mapper view: Show all 6 pin types
    switch (venue.venuePinType) {
      case VenuePinType.Green:
        return GreenPin;
      case VenuePinType.Black:
        return BlackPin;
      case VenuePinType.Blue:
        return BluePin;
      case VenuePinType.Purple:
        return PurplePin;
      case VenuePinType.Orange:
        return OrangePin;
      case VenuePinType.Grey:
        return GreyPin;
      default:
        return GreyPin;
    }
  };

  const getSelectedPinIcon = (venue: Venue) => {
    // Classic flat pin, no border — see getPinIcon above.
    return hasVenueData(venue) ? OrangePin : GreyPin;

    // // Mapper view: Show all pin types (selected variants only for Grey/Orange)
    // switch (venue.venuePinType) {
    //   case VenuePinType.Grey:
    //     return GreyPinSelected;
    //   case VenuePinType.Orange:
    //     return OrangePinSelected;
    //   case VenuePinType.Green:
    //     return GreenPin;
    //   case VenuePinType.Black:
    //     return BlackPin;
    //   case VenuePinType.Blue:
    //     return BluePin;
    //   case VenuePinType.Purple:
    //     return PurplePin;
    //   default:
    //     return GreyPinSelected;
    // }
  };

  const radius = 75; // default radius in km
  const unit = "km"; // or'miles'
  const toRadians = (degrees) => {
    return degrees * (Math.PI / 180);
  };

  // A viewport-bounded search that returns nothing widens out: clear the bounds
  // and re-search so the backend expands its radius (never loops — the follow-up
  // has no bounds; guarded per result-set).
  const autoExpandedForDataRef = useRef<any>(null);
  useEffect(() => {
    if (!isSearchRoute || !mapRef) return;
    if (!venuesData) return;
    if (locations.length > 0) return;
    if (!searchPageParams.map_ne_lat) return;
    if (autoExpandedForDataRef.current === venuesData) return;
    autoExpandedForDataRef.current = venuesData;

    setSearchPageParams((prev) => ({
      ...prev,
      map_ne_lat: undefined,
      map_ne_lng: undefined,
      map_sw_lat: undefined,
      map_sw_lng: undefined,
      searchThisAreaClicked: "no",
      autoZoom: "yes",
      unixTimestamp: Math.floor(Date.now() / 1000),
    }));
  }, [
    venuesData,
    locations.length,
    searchPageParams.map_ne_lat,
    isSearchRoute,
    mapRef,
    setSearchPageParams,
  ]);

  // Ref to track which venuesData we already auto-zoomed for, so we don't
  // re-run auto-zoom (and trigger another API call) for the same result set
  const lastAutoZoomedDataRef = useRef<any>(null);

  // Effect to auto-update zoom level based on search_distance_used (gated by flag)
  useEffect(() => {
    console.log("search_distance_used", venuesData?.search_distance_used);
    console.log(
      "search_original_distance",
      venuesData?.search_original_distance,
    );

    // Only auto-zoom when explicitly flagged by search click
    if (mapRef && enableAutoZoom && searchPageParams.autoZoom === "yes") {
      // Skip if we already auto-zoomed for this exact data set
      if (venuesData === lastAutoZoomedDataRef.current) {
        return;
      }
      lastAutoZoomedDataRef.current = venuesData;

      // Case 1: API returned different distances - adjust zoom based on search_distance_used
      if (
        venuesData?.search_distance_used &&
        venuesData.search_distance_used !== venuesData.search_original_distance
      ) {
        console.log(
          "Received modified search distance:",
          venuesData.search_distance_used,
        );

        const distanceInKm = venuesData.search_distance_used;

        // Calculate the appropriate zoom level
        const newZoomLevel = calculateZoomFromDistance(distanceInKm);
        console.log(
          "Calculated zoom level:",
          newZoomLevel,
          "from distance:",
          distanceInKm,
          "km",
        );

        // Only update if we need to zoom OUT (expand view)
        // Lower zoom number = zoomed out (wider area), higher = zoomed in (closer)
        // Only allow zooming out, never zoom in
        const currentZoom = mapRef.getZoom();
        if (newZoomLevel < currentZoom) {
          console.log("Zooming OUT from", currentZoom, "to", newZoomLevel);

          // Only update the map visually ï¿½ do NOT call setSearchPageParams
          // to avoid triggering another API call
          mapRef.setZoom(newZoomLevel);
        } else {
          console.log(
            "Skipping zoom IN - current zoom",
            currentZoom,
            "is already closer than",
            newZoomLevel,
          );
        }
      }
      // Case 2: No distance information - use fitBounds to show all venues (only zoom OUT, never IN)
      else if (
        venuesData &&
        locations.length > 0 &&
        !venuesData.search_distance_used &&
        !venuesData.search_original_distance
      ) {
        console.log(
          "No distance information received, checking if fitBounds should apply",
        );

        // Zoom constraints (same as home page)
        const MIN_ZOOM = 11; // Don't zoom out beyond this
        const MAX_ZOOM = 16; // Don't zoom in beyond this

        try {
          const currentZoom = mapRef.getZoom();
          console.log("Current zoom level:", currentZoom);

          // Create bounds from all venue locations
          const bounds = new google.maps.LatLngBounds();
          locations.forEach((venue) => {
            if (venue.latitude && venue.longitude) {
              bounds.extend(
                new google.maps.LatLng(venue.latitude, venue.longitude),
              );
            }
          });

          if (bounds.isEmpty()) {
            console.log("Bounds are empty, skipping fitBounds");
            return;
          }

          // Calculate the distance/spread of venues to estimate zoom level
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();

          // Calculate approximate distance in km using Haversine formula
          const latDiff = ne.lat() - sw.lat();
          const lngDiff = ne.lng() - sw.lng();
          const avgLat = (ne.lat() + sw.lat()) / 2;

          // Rough distance calculation (good enough for zoom estimation)
          const latDistance = latDiff * 111; // 1 degree lat ï¿½ 111 km
          const lngDistance =
            lngDiff * 111 * Math.cos((avgLat * Math.PI) / 180);
          const maxDistance = Math.max(latDistance, lngDistance);

          // Calculate what zoom level would be appropriate for this distance
          const estimatedZoom = calculateZoomFromDistance(maxDistance * 1.2); // Add 20% margin
          console.log(
            "Estimated zoom for bounds:",
            estimatedZoom,
            "from distance:",
            maxDistance.toFixed(2),
            "km",
          );

          // Only apply fitBounds if it would zoom OUT (lower number = more zoomed out)
          // or if this is the initial load (currentZoom is undefined)
          if (currentZoom === undefined || estimatedZoom < currentZoom) {
            console.log(
              "Applying fitBounds - zooming OUT from",
              currentZoom,
              "to approximately",
              estimatedZoom,
            );

            // Apply fitBounds with padding
            mapRef.fitBounds(bounds, {
              top: 50,
              right: 50,
              bottom: 50,
              left: 50,
            });

            // Enforce min/max zoom constraints after fitBounds
            setTimeout(() => {
              const actualZoom = mapRef.getZoom();
              if (actualZoom !== undefined) {
                if (actualZoom < MIN_ZOOM) {
                  mapRef.setZoom(MIN_ZOOM);
                  console.log("Zoom limited to minimum:", MIN_ZOOM);
                } else if (actualZoom > MAX_ZOOM) {
                  mapRef.setZoom(MAX_ZOOM);
                  console.log("Zoom limited to maximum:", MAX_ZOOM);
                } else {
                  console.log("Dynamic zoom applied:", actualZoom);
                }
              }
            }, 50); // Minimal delay to allow fitBounds to complete
          } else {
            console.log(
              "Respecting manual zoom - staying at",
              currentZoom,
              "(fitBounds would zoom IN to approximately",
              estimatedZoom,
              ")",
            );
          }
        } catch (error) {
          console.error("Error applying dynamic fitBounds zoom:", error);
        }
      }
    }
  }, [venuesData, locations.length, mapRef, searchPageParams.autoZoom]);

  // After an external search's auto-zoom settles, realign the zoom-in autoload
  // baseline to the actual (possibly zoomed-out) zoom, so the next 1-level
  // zoom-in triggers a reload. Runs only while autoZoom is "yes" (initial /
  // external searches); progressive reloads set autoZoom "no".
  useEffect(() => {
    if (!mapRef || searchPageParams.autoZoom !== "yes" || !venuesData) return;
    const t = setTimeout(() => {
      const z = mapRef.getZoom();
      if (typeof z === "number") lastReloadedZoomRef.current = z;
    }, 150);
    return () => clearTimeout(t);
  }, [venuesData, mapRef, searchPageParams.autoZoom]);

  // Fit the map to the result pins after a deliberate area/category search
  // (e.g. "cafe in pinner"). Driven by fitMapToResultsAtom rather than the
  // autoZoom flag, so it works even when VITE_ENABLE_AUTO_ZOOM is disabled.
  // Waits for fresh data (different ref than at request time) so it never frames
  // the previous search's pins, and clamps to sane min/max zoom.
  const pendingFitRef = useRef(false);
  const lastFitRequestRef = useRef(0);
  const fitBaselineDataRef = useRef<unknown>(null);
  useEffect(() => {
    if (fitMapToResults !== 0 && fitMapToResults !== lastFitRequestRef.current) {
      lastFitRequestRef.current = fitMapToResults;
      pendingFitRef.current = true;
      fitBaselineDataRef.current = venuesData;
    }
    if (!pendingFitRef.current || !mapRef) return;
    // Hold until the new search's data has actually loaded.
    if (venuesData === fitBaselineDataRef.current || locations.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    locations.forEach((v) => {
      if (v.latitude && v.longitude) {
        bounds.extend(new google.maps.LatLng(v.latitude, v.longitude));
      }
    });
    if (bounds.isEmpty()) return;

    pendingFitRef.current = false;
    mapRef.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });

    const MIN_ZOOM = 11;
    const MAX_ZOOM = 16;
    window.setTimeout(() => {
      const z = mapRef.getZoom();
      if (z === undefined) return;
      if (z < MIN_ZOOM) mapRef.setZoom(MIN_ZOOM);
      else if (z > MAX_ZOOM) mapRef.setZoom(MAX_ZOOM);
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitMapToResults, venuesData, locations.length, mapRef]);

  // Effect to pan the map to the first result for expand_radius searches
  // This is separate from auto-zoom ï¿½ it only handles map centering
  useEffect(() => {
    if (
      mapRef &&
      searchPageParams.search_type === "expand_radius" &&
      locations.length > 0
    ) {
      const firstResult = locations[0];
      if (firstResult?.latitude && firstResult?.longitude) {
        const resultCenter = {
          lat: firstResult.latitude,
          lng: firstResult.longitude,
        };
        console.log("expand_radius: Panning map to first result", resultCenter);
        mapRef.panTo(resultCenter);
        setMapCenter(resultCenter);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, mapRef, searchPageParams.search_type]);

  // Reset map initialization flag when location changes significantly
  // This allows onMapMoved to be skipped during the next map load
  useEffect(() => {
    // Reset the flag when searchPageParams location changes
    // This ensures we skip onMapMoved during the new map initialization
    mapHasCompletedInitialLoadRef.current = false;
    lastInitializedParamsRef.current = { lat: null, lng: null };
    console.log("Location changed - resetting map initialization flags");
  }, [searchPageParams.latitude, searchPageParams.longitude]);

  useEffect(() => {
    if (
      locations.length &&
      mapRef &&
      userLocation &&
      savedPlaceIdToView !== ""
    ) {
      const calculateDistance = (lat1, lon1, lat2, lon2, unit) => {
        const R = unit === "miles" ? 3958.8 : 6371; // Radius of the Earth in miles or km
        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRadians(lat1)) *
            Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        return distance;
      };

      const filterLocationsByRadius = (locations, center, radius, unit) => {
        return locations.filter((location) => {
          const distance = calculateDistance(
            center.latitude,
            center.longitude,
            location.latitude,
            location.longitude,
            unit,
          );
          return distance <= radius;
        });
      };

      const filteredLocations = filterLocationsByRadius(
        locations,
        userLocation,
        radius,
        unit,
      );
      const bounds = new google.maps.LatLngBounds();
      filteredLocations.forEach((location) => {
        bounds.extend(
          new google.maps.LatLng(location.latitude, location.longitude),
        );
      });

      bounds.extend(
        new google.maps.LatLng(userLocation.latitude, userLocation.longitude),
      );

      mapRef.fitBounds(bounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, userLocation, radius, unit, mapRef]);

  // code to show venue marker highlighted
  useEffect(() => {
    if (selectedVenue && savedPlaceIdToView == "" && mapRef) {
      const newPosition = new google.maps.LatLng({
        lat: selectedVenue.latitude,
        lng: selectedVenue.longitude,
      });
      mapRef.panTo(newPosition);
      setActiveMarker(selectedVenue.id);
    } else {
      setActiveMarker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue, mapRef]);

  // Tracked rather than read once, so a resize flips clustering on/off.
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(DESKTOP_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktopViewport(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  // Two map modes (search route only): "all" shows native Google business POIs
  // (clickable -> grey pin) and hides our pins; "sociability" (default) hides all
  // POIs for a clean map and plots our mapped venues. Off the search route, keep
  // the previous behaviour (business POIs shown + clickable when the feature is on).
  const isAllMode = isSearchRoute && searchPageParams.map_mode === "all";
  // "declutter": same mapped venues as sociability, but drawn as individual pins
  // that thin out by zoom via Google's collision engine (no clustering).
  const isDeclutterMode =
    isSearchRoute && searchPageParams.map_mode === "declutter";
  // "overview": all venues, dots at low zoom / pins at high zoom, never clustered.
  const isOverviewMode =
    isSearchRoute && searchPageParams.map_mode === "overview";
  // "sociability" ("Places with data") is the default when none of the above apply.
  const isSociabilityMode =
    isSearchRoute && !isAllMode && !isDeclutterMode && !isOverviewMode;
  // Venue detail clusters its nearby pins on desktop only; the mobile map is too
  // small to zoom into a bubble.
  const clusterVenueDetails =
    props.mapSource === "venuedetails" && isDesktopViewport;
  const usesDotPinTransition =
    isOverviewMode || isAllMode || isSociabilityMode || clusterVenueDetails;
  // Few results don't need clusters/dots — show them all as pins at any zoom.
  const fewResults = locations.length <= FEW_RESULTS_PIN_THRESHOLD;
  const showNativePois = isSearchRoute ? isAllMode : enableGooglePoiClick;
  const poiClickable = enableGooglePoiClick && showNativePois;

  // One-time hint, only shown on the mode it asks the user to switch away from.
  const [allPlacesHintDismissed, setAllPlacesHintDismissed] = useAtom(
    allPlacesHintDismissedAtom,
  );
  const [allPlacesHintHeight, setAllPlacesHintHeight] = useState(0);
  const showAllPlacesHint =
    isSearchRoute &&
    enableGooglePoiClick &&
    !selectedVenue &&
    props.mapSource !== "venuedetails" &&
    isSociabilityMode &&
    !allPlacesHintDismissed;

  // Map mode toggle ("Places with data" / "All places"): search route only,
  // behind the POI-click flag, and hidden while a venue detail is open.
  const showMapModeToggle =
    Boolean(isSearchRoute) &&
    enableGooglePoiClick &&
    !selectedVenue &&
    props.mapSource !== "venuedetails";

  const showRefreshMapButton = savedPlaceIdToView === "" && !poiResolving;

  // Switching to "All places" retires the hint too, however the user got there.
  useEffect(() => {
    if (isAllMode && !allPlacesHintDismissed) {
      setAllPlacesHintDismissed(true);
    }
  }, [isAllMode, allPlacesHintDismissed, setAllPlacesHintDismissed]);
  // Vector maps ignore JS `styles`, so POI visibility is controlled by the Map
  // ID's cloud style. Default googleMapId (POIs hidden) is used everywhere
  // except "All places", which swaps to a Map ID that shows/allows clicking POIs.
  // All places: POI map id, dark variant when dark.
  const activeMapId =
    isAllMode && googleMapIdPoi
      ? (isDark && googleMapDarkModeIdPoi ? googleMapDarkModeIdPoi : googleMapIdPoi)
      : googleMapId;
  const baseStyles = isDark ? darkModeStyles : lightModeStyles;
  const poiStyles = enableGooglePoiClick
    ? [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        ...(showNativePois
          ? [{ featureType: "poi.business", stylers: [{ visibility: "on" }] }]
          : []),
      ]
    : [];
  const mapOptions = {
    keyboardShortcuts: false,
    fullscreenControl: false,
    disableDefaultUI: true,
    clickableIcons: poiClickable,
    styles: [...((baseStyles as any[]) || []), ...poiStyles],
  };

  // ---- Marker clustering: "Places with data", "All places", venue detail ----
  const shouldCluster =
    enableAdvancedMarkers &&
    (isSociabilityMode || isAllMode || clusterVenueDetails);
  // The dots/labels ladder below stays specific to "Places with data".
  const sociabilityLadder = isSociabilityMode && enableAdvancedMarkers;
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const [clusterMarkers, setClusterMarkers] = useState<
    Record<string, google.maps.marker.AdvancedMarkerElement>
  >({});
  // Current zoom — drives when always-on name labels appear (clusters dissolved).
  const [mapZoom, setMapZoom] = useState<number>(16);
  // Separate collision name markers only in clustered "Places with data" mode
  // (pins there don't collide, so names need independent decluttering).
  // Gated per-venue below (against its own pin flip zoom) rather than a flat
  // zoom floor, so names appear as soon as a venue flips to a pin — matching
  // native's label-follows-pin behaviour instead of lagging behind it.
  const showNameLabels = sociabilityLadder;
  // Explore / All places draw names inline on the pin, revealed at higher zoom
  // so lower zooms stay uncluttered and Google's collision thins pins+names.
  const showInlineNames =
    (isDeclutterMode || isAllMode || isOverviewMode) &&
    mapZoom >= LABEL_MIN_ZOOM;
  // Per-venue dot -> pin "flip zoom", ranked by review count (most-reviewed
  // first). Each mode uses its own zoom window.
  const overviewPinFlipZoom = useMemo(() => {
    const map = new Map<string, number>();
    if (!usesDotPinTransition) return map;
    const dotStartZoom = isSociabilityMode
      ? SOCIABILITY_DOT_START_ZOOM
      : OVERVIEW_TRANSITION_START_ZOOM;
    const pinMinZoom = isSociabilityMode
      ? SOCIABILITY_PIN_MIN_ZOOM
      : OVERVIEW_PIN_MIN_ZOOM;
    const ranked = [...locations].sort((a, b) => {
      const scoreA = a.reviewsSubmitted || a.reviewsDetailsSubmitted || 0;
      const scoreB = b.reviewsSubmitted || b.reviewsDetailsSubmitted || 0;
      return scoreB - scoreA;
    });
    // Sociability front-loads a top slice so pins appear from the first dot
    // zoom (no dots-only level); the rest ramp across the window.
    const head = isSociabilityMode ? SOCIABILITY_PIN_HEAD_FRACTION : 0;
    const n = ranked.length;
    ranked.forEach((location, idx) => {
      const fraction = n > 1 ? idx / (n - 1) : 0;
      const ramp =
        head >= 1 ? 0 : Math.max(0, (fraction - head) / (1 - head));
      map.set(location.id, dotStartZoom + ramp * (pinMinZoom - dotStartZoom));
    });
    return map;
  }, [usesDotPinTransition, isSociabilityMode, locations]);

  // Which Sociability venues actually get a name label, once eligible by
  // zoom/focus (see overviewPinFlipZoom above). Web Mercator world-pixel
  // projection at the current zoom, then a greedy keep-if-far-enough-away
  // pass (highest priority first) so labels stop stacking on top of each
  // other in dense areas — Google's own collision doesn't catch this (see
  // LABEL_MIN_SEPARATION_PX).
  const visibleLabelIds = useMemo(() => {
    if (!showNameLabels) return null;
    const scale = 256 * 2 ** mapZoom;
    const toWorldPixel = (lat: number, lng: number) => {
      const sinLat = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
      return {
        x: (0.5 + lng / 360) * scale,
        y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
      };
    };
    const candidates = locations.filter((location) => {
      if (!location.name) return false;
      const isGreyPin = location.venuePinType === VenuePinType.Grey;
      const isFocused =
        selectedVenue?.id === location.id ||
        (activeMarker === location.id && snippetOpen) ||
        pinnedFirstVenue?.id === location.id;
      if (isGreyPin && !isFocused) return false;
      const flipZoom =
        overviewPinFlipZoom.get(location.id) ?? SOCIABILITY_PIN_MIN_ZOOM;
      return isFocused || fewResults || mapZoom >= flipZoom;
    });
    // locations is already priority-ordered (nearest/most-reviewed first);
    // just float focused venues to the front so they always win the gap check.
    const ranked = [...candidates].sort((a, b) => {
      const aFocused =
        selectedVenue?.id === a.id ||
        (activeMarker === a.id && snippetOpen) ||
        pinnedFirstVenue?.id === a.id;
      const bFocused =
        selectedVenue?.id === b.id ||
        (activeMarker === b.id && snippetOpen) ||
        pinnedFirstVenue?.id === b.id;
      return (bFocused ? 1 : 0) - (aFocused ? 1 : 0);
    });
    const kept: { x: number; y: number }[] = [];
    const ids = new Set<string>();
    ranked.forEach((location) => {
      const point = toWorldPixel(location.latitude, location.longitude);
      const isFocused =
        selectedVenue?.id === location.id ||
        (activeMarker === location.id && snippetOpen) ||
        pinnedFirstVenue?.id === location.id;
      const tooClose = kept.some(
        (k) => Math.hypot(k.x - point.x, k.y - point.y) < LABEL_MIN_SEPARATION_PX,
      );
      if (isFocused || !tooClose) {
        kept.push(point);
        ids.add(location.id);
      }
    });
    return ids;
  }, [
    showNameLabels,
    mapZoom,
    locations,
    overviewPinFlipZoom,
    fewResults,
    selectedVenue,
    activeMarker,
    snippetOpen,
    pinnedFirstVenue,
  ]);

  // Register/unregister a pin's marker element with the clusterer.
  const setClusterMarkerRef = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement | null, key: string) => {
      setClusterMarkers((prev) => {
        if ((marker && prev[key]) || (!marker && !prev[key])) return prev;
        if (marker) return { ...prev, [key]: marker };
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // Stable per-key ref callbacks so steady-state renders (hover/snippet) don't
  // re-register markers and thrash the clusterer.
  const markerRefCbs = useRef<
    Map<string, (m: google.maps.marker.AdvancedMarkerElement | null) => void>
  >(new Map());
  const getClusterMarkerRef = useCallback(
    (key: string) => {
      let cb = markerRefCbs.current.get(key);
      if (!cb) {
        cb = (m: google.maps.marker.AdvancedMarkerElement | null) =>
          setClusterMarkerRef(m, key);
        markerRefCbs.current.set(key, cb);
      }
      return cb;
    },
    [setClusterMarkerRef],
  );

  // Create / tear down the clusterer. Always rebuilt on `mapRef` change:
  // swapping activeMapId (e.g. "Places with data" <-> "All places") forces
  // vis.gl to recreate the underlying google.maps.Map, so a clusterer bound
  // to the old instance would otherwise keep its markers silently detached.
  useEffect(() => {
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.setMap(null);
      clustererRef.current = null;
    }
    if (!mapRef || !shouldCluster) return;
    clustererRef.current = new MarkerClusterer({
      map: mapRef,
      renderer: makeClusterRenderer(isDark),
      onClusterClick: handleClusterClick,
      algorithm: new SuperClusterAlgorithm({
        radius: CLUSTER_RADIUS,
        maxZoom: Math.ceil(CLUSTER_MAX_ZOOM),
      }),
    });
    // Re-attach markers already registered this render (the sync effect
    // below only fires on `clusterMarkers` changes).
    clustererRef.current.addMarkers(Object.values(clusterMarkers));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clusterMarkers read intentionally, not tracked
  }, [mapRef, shouldCluster, isDark]);

  // Keep the clusterer's markers in sync with the rendered venue pins.
  useEffect(() => {
    const clusterer = clustererRef.current;
    if (!clusterer) return;
    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(clusterMarkers));
  }, [clusterMarkers]);

  return (
    <div
      className={cn(
        "flex-1 relative h-full rounded-[12px] overflow-clip",
        props.hidden ? "hidden" : "block",
      )}
    >
      {isMapsLoaded && (
        enableAdvancedMarkers ? (
          /* ---- vis.gl Advanced Markers path ---- */
          <APIProvider apiKey={googleMapKey} libraries={["marker", "geometry"]} version="3.55">
            <VisGlMap
              mapId={activeMapId}
              colorScheme={isDark ? "DARK" : "LIGHT"}
              style={{ width: "100%", height: "100%" }}
              defaultCenter={mapCenter}
              defaultZoom={16}
              gestureHandling="greedy"
              disableDefaultUI
              keyboardShortcuts={false}
              clickableIcons={poiClickable}
              onClick={(e: VisGlMapMouseEvent) => {
                if (poiClickable && e.detail.placeId) {
                  e.stop();
                  handleCloseSnippet();
                  handleClosePoi();
                  setLocationSearchAnchor(null);
                  const placeId = e.detail.placeId;
                  if (isSearchRoute && isAllMode) {
                    handlePoiContribution(
                      placeId,
                      e.detail.latLng?.lat ?? undefined,
                      e.detail.latLng?.lng ?? undefined,
                      { fromPinClick: true },
                    );
                  } else {
                    resolvePlaceAndShow(placeId, {
                      center: false,
                      messageLat: e.detail.latLng?.lat ?? undefined,
                      messageLng: e.detail.latLng?.lng ?? undefined,
                    });
                  }
                  return;
                }
                setActiveMarker(null);
                setSnippetPosition(null);
                setTooltipVenue(null);
                setHoveredMarkerId(null);
                setHoveredVenueId("");
                setSnippetOpen(false);
                setLocationSearchAnchor(null);
                handleClosePoi();
              }}
              onIdle={() => onMapMoved()}
              onZoomChanged={() => {
                const z = mapRef?.getZoom();
                if (typeof z === "number") setMapZoom(z);
                setActiveMarker(null);
                setSnippetPosition(null);
                setTooltipVenue(null);
                setHoveredMarkerId(null);
                setSnippetOpen(false);
              }}
              onDragstart={() => {
                // Panning keeps the snippet and the centre-location marker;
                // only the transient hover tooltip goes.
                setTooltipVenue(null);
                setHoveredMarkerId(null);
              }}
            >
              {/* Sync vis.gl map instance into the shared mapRef */}
              <MapRefCapture onMap={setMapRef} />

              {/* Venue markers. In "all" mode our mapped (orange) pins render on
                  top of the native Google POIs so users can spot venues with
                  data; grey (no-data) venues are hidden since the Google POI
                  already represents them. Sociability/declutter modes only
                  show "places with data", so grey venues never render there
                  either — this avoids a flash of grey pins while data loads. */}
              {locations.map((location, index) => {
                const isSelected = !!(
                  selectedVenue && selectedVenue.id === location.id
                );
                // The venue whose snippet is open, or that was picked from
                // autosuggest, must always show a highlighted pin — even when it
                // would normally be a low-zoom dot or a grey venue hidden behind
                // the native Google POI.
                const isSnippetPin =
                  activeMarker === location.id && snippetOpen;
                const isFocusPlace =
                  pinnedFirstVenue?.id === location.id ||
                  isFocusedPlace(location);
                // Keeps the searched venue on the map as a pin...
                const isFocused = isSelected || isSnippetPin || isFocusPlace;
                // ...but only highlighted until another pin is clicked.
                const isHighlighted =
                  isSelected || isSnippetPin || (isFocusPlace && !focusPinRetired);
                if (
                  (showNativePois || isSociabilityMode || isDeclutterMode) &&
                  location.venuePinType === VenuePinType.Grey &&
                  !isFocused
                ) {
                  return null;
                }
                // Hover still works for other pins while a snippet is open;
                // only the pin whose snippet is open skips its own hover state.
                const isHovered =
                  (hoveredMarkerId === location.id ||
                    hoveredVenueId === location.id) &&
                  !(snippetOpen && activeMarker === location.id);

                // Shared handler used by both the outer AdvancedMarker (gmp-click /
                // gmpClickable keyboard path) and the inner AdvancedPinContent div
                // (tabIndex keyboard path).  The content div calls stopPropagation so
                // mouse clicks only fire once.
                const handleVenueMarkerClick = () =>
                  triggerVenueMarkerClick(location, index);

                // Collision priority via zIndex: selected/hovered on top, then
                // orange ("has data") above grey, then nearer (earlier) above later.
                // In declutter/all-places/overview mode priority is the review count
                // so the most-reviewed venues win Google's collision (like POI
                // prominence), and pins decluster by zoom instead of always-on/off.
                const isGreyPin = location.venuePinType === VenuePinType.Grey;
                const reviewScore =
                  location.reviewsSubmitted ||
                  location.reviewsDetailsSubmitted ||
                  0;
                const collisionPriority =
                  isDeclutterMode || isAllMode || isOverviewMode
                    ? reviewScore
                    : (isGreyPin ? 0 : 100000) + (locations.length - index);
                const markerZIndex = isHighlighted
                  ? SELECTED_MARKER_ZINDEX
                  : isHovered
                    ? 999999
                    : collisionPriority;

                // A venue is a dot until its ranked flip zoom; top-ranked ones
                // flip to pins early (from zoom 16). Focused venues are always
                // pins. Dots still register with the clusterer while in the
                // cluster phase, so dense dots group into bubbles.
                const inClusterPhase =
                  shouldCluster && mapZoom <= CLUSTER_MAX_ZOOM;
                const isOverviewDot =
                  usesDotPinTransition &&
                  !isFocused &&
                  !fewResults &&
                  mapZoom <
                    (overviewPinFlipZoom.get(location.id) ??
                      OVERVIEW_PIN_MIN_ZOOM);
                // Register only dots that are still in the cluster phase.
                const isClustererMarker =
                  shouldCluster && inClusterPhase && isOverviewDot;
                // Registered markers key "cl"; others "un" so a state flip
                // remounts a fresh vis.gl marker (else the clusterer leaves
                // .map null and the marker vanishes).
                const markerKey = `adv-marker-${location.id}-${
                  isClustererMarker ? "cl" : "un"
                }`;
                if (isOverviewDot) {
                  const isDotHovered =
                    (hoveredMarkerId === location.id ||
                      hoveredVenueId === location.id) &&
                    !(snippetOpen && activeMarker === location.id);
                  const onDotEnter = () => {
                    if (snippetOpen && activeMarker === location.id) return;
                    setHoveredVenueId(location.id);
                    setHoveredMarkerId(location.id);
                  };
                  const onDotLeave = () => {
                    if (snippetOpen && activeMarker === location.id) return;
                    setHoveredVenueId("");
                    setHoveredMarkerId(null);
                  };
                  return (
                    <AdvancedMarker
                      key={markerKey}
                      ref={
                        isClustererMarker
                          ? getClusterMarkerRef(`adv-marker-${location.id}`)
                          : undefined
                      }
                      position={{ lat: location.latitude, lng: location.longitude }}
                      zIndex={isDotHovered ? 999999 : markerZIndex}
                      onClick={handleVenueMarkerClick}
                    >
                      {isDotHovered ? (
                        // Hover: dot becomes a pin + name. Centre the pin on the
                        // dot's point (shift down half its height) so the cursor
                        // stays inside and doesn't flicker back to the dot.
                        <div
                          style={{
                            transform: `translateY(${
                              (PIN_SIZE_ACTIVE * PIN_ASPECT) / 2
                            }px)`,
                          }}
                        >
                          <AdvancedPinContent
                            venue={location}
                            isSelected={false}
                            isHovered
                            isMapperView={isMapperViewActive}
                            showLabel
                            onMouseEnter={onDotEnter}
                            onMouseLeave={onDotLeave}
                          />
                        </div>
                      ) : (
                        <OverviewDot
                          color={getVenuePinColor(
                            location.venuePinType,
                            isMapperViewActive,
                            isDark,
                            hasVenueData(location),
                          )}
                          name={location.name}
                          isHovered={false}
                          onMouseEnter={onDotEnter}
                          onMouseLeave={onDotLeave}
                        />
                      )}
                    </AdvancedMarker>
                  );
                }

                return (
                  <AdvancedMarker
                    key={markerKey}
                    position={{ lat: location.latitude, lng: location.longitude }}
                    zIndex={markerZIndex}
                    collisionBehavior={
                      isAllMode && !isGreyPin
                        ? // Orange pin hides the matching native Google POI beneath it.
                          google.maps.CollisionBehavior
                            .REQUIRED_AND_HIDES_OPTIONAL
                        : isDeclutterMode || isAllMode || isOverviewMode
                          ? google.maps.CollisionBehavior
                              .OPTIONAL_AND_HIDES_LOWER_PRIORITY
                          : undefined
                    }
                    onClick={handleVenueMarkerClick}
                  >
                    <AdvancedPinContent
                      venue={location}
                      isSelected={isHighlighted}
                      isHovered={isHovered}
                      isMapperView={isMapperViewActive}
                      showLabel={showInlineNames}
                      suppressLabel={sociabilityLadder && !isMapperViewActive}
                      suppressHoverLabel={sociabilityLadder && !isMapperViewActive}
                      onMouseEnter={() => {
                        if (snippetOpen && activeMarker === location.id) return;
                        setTooltipVenue(location);
                        setHoveredVenueId(location.id);
                        setHoveredMarkerId(location.id);
                      }}
                      onMouseLeave={() => {
                        if (snippetOpen && activeMarker === location.id) return;
                        setTooltipVenue(null);
                        setHoveredVenueId("");
                        setHoveredMarkerId(null);
                      }}
                    />
                  </AdvancedMarker>
                );
              })}

              {/* Name labels as separate OPTIONAL markers, so collision can
                  declutter names independently of pins. Post-cluster only. */}
              {showNameLabels &&
                locations.map((location, index) => {
                  if (!location.name) return null;
                  const isSelected = !!(
                    selectedVenue && selectedVenue.id === location.id
                  );
                  const isGreyPin =
                    location.venuePinType === VenuePinType.Grey;
                  // Mirror the pin's own grey-hide rule (sociability/declutter
                  // never show grey venues) so a label never floats alone.
                  const isFocused =
                    isSelected ||
                    (activeMarker === location.id && snippetOpen) ||
                    pinnedFirstVenue?.id === location.id;
                  if (isGreyPin && !isFocused) return null;
                  // Eligible (flipped-to-pin, or focused/fewResults) AND not
                  // too close to a higher-priority label already kept — see
                  // visibleLabelIds above.
                  if (!visibleLabelIds?.has(location.id)) return null;
                  // Same priority ranking as the pins, minus 1 so the pin
                  // itself always wins a tie (label sits exactly on top of it).
                  const labelZIndex =
                    (isSelected
                      ? SELECTED_MARKER_ZINDEX
                      : (isGreyPin ? 0 : 100000) + (locations.length - index)) - 1;
                  const category =
                    location.calcVenueType || location.businessType || "";
                  // AdvancedMarkers anchor bottom-center by default, so a
                  // same-size invisible spacer reproduces the pin's anchor
                  // frame, letting the label position off it like the inline
                  // label in AdvancedPinContent.
                  const pinSize = isSelected ? PIN_SIZE_SELECTED : PIN_SIZE_DEFAULT;
                  const pinHeight = pinSize * PIN_ASPECT;
                  const pinCentreTop = getPinCentreTop(pinSize);
                  return (
                    <AdvancedMarker
                      key={`adv-label-${location.id}`}
                      position={{
                        lat: location.latitude,
                        lng: location.longitude,
                      }}
                      zIndex={labelZIndex}
                      collisionBehavior={
                        google.maps.CollisionBehavior
                          .OPTIONAL_AND_HIDES_LOWER_PRIORITY
                      }
                      // Same click as the pin — a safety net in case a click
                      // lands on the label instead of the pin underneath.
                      onClick={() => triggerVenueMarkerClick(location, index)}
                    >
                      <div
                        className="relative"
                        style={{
                          width: pinSize,
                          height: pinHeight,
                          pointerEvents: "none",
                        }}
                      >
                        {/* Name + category, right of the pin (Google POI style). */}
                        <div
                          className="absolute left-full ml-1.5 flex flex-col select-none"
                          style={{
                            top: pinCentreTop,
                            transform: "translateY(-55%)",
                          }}
                        >
                          <span
                            className="text-[13px] font-medium leading-tight whitespace-nowrap max-w-[160px] truncate"
                            style={{ textShadow: NAME_HALO, color: NAME_TEXT_COLOR }}
                          >
                            {location.name}
                          </span>
                          {category && (
                            <span
                              className="text-[11px] leading-tight whitespace-nowrap max-w-[160px] truncate"
                              style={{
                                textShadow: NAME_HALO,
                                color: CATEGORY_TEXT_COLOR,
                              }}
                            >
                              {category}
                            </span>
                          )}
                        </div>
                      </div>
                    </AdvancedMarker>
                  );
                })}

              {/* Reference-point marker for a selected location/area/landmark
                  suggestion that has no venue of its own (e.g. "Tottenham Court
                  Road", a museum) — anchors the map view to the search. */}
              {locationSearchAnchor && (
                <AdvancedMarker
                  key="location-search-anchor"
                  position={{
                    lat: locationSearchAnchor.lat,
                    lng: locationSearchAnchor.lng,
                  }}
                  zIndex={CENTRE_LOCATION_ZINDEX}
                >
                  <div className="flex flex-col items-center pointer-events-none">
                    <img
                      src={CentreLocation}
                      width={18}
                      height={36}
                      alt=""
                      draggable={false}
                      style={{
                        display: "block",
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))",
                      }}
                    />
                    <span
                      className="label-s-medium px-2 py-0.5 rounded-full bg-background text-fg-neutral-strong shadow-md -mt-1 max-w-[160px] truncate"
                    >
                      {locationSearchAnchor.label}
                    </span>
                  </div>
                </AdvancedMarker>
              )}

              {/* POI-resolved venue markers */}
              {resolvedPois
                .filter(
                  (v) =>
                    !locations.some(
                      (l) =>
                        (v.placeId && l.placeId === v.placeId) || l.id === v.id,
                    ),
                )
                // Grey (unmapped) venues belong to "All places" only — hide them
                // after switching back to a mapped-only mode (e.g. Sociability).
                .filter((v) => isAllMode || v.venuePinType !== VenuePinType.Grey)
                // Grey (no accessibility data) venues duplicate the native Google
                // POI icon/label whenever POIs are visible, without adding any
                // value to the user — skip rendering our own pin for them, unless
                // this is the focused venue (selected / snippet / autosuggest
                // pick), which must stay visible even if the native POI is hidden.
                .filter(
                  (v) =>
                    !(showNativePois && v.venuePinType === VenuePinType.Grey) ||
                    activeMarker === v.id ||
                    pinnedFirstVenue?.id === v.id ||
                    selectedVenue?.id === v.id,
                )
                .map((poiVenue) => {
                  const handlePoiMarkerClick = () => {
                    if (
                      !isFocusedPlace(poiVenue) &&
                      pinnedFirstVenue?.id !== poiVenue.id
                    ) {
                      setFocusPinRetired(true);
                    }
                    if (
                      currentVenueTypePage === 1 ||
                      props.mapSource === "venuedetails"
                    ) {
                      handleLoadVenue(poiVenue);
                    } else {
                      setActiveMarker(poiVenue.id);
                      setSnippetPosition(getSnippetPosition(poiVenue));
                      setSnippetOpen(true);
                    }
                  };
                  const isPoiHovered =
                    (hoveredMarkerId === poiVenue.id ||
                      hoveredVenueId === poiVenue.id) &&
                    !(snippetOpen && activeMarker === poiVenue.id);
                  return (
                    <AdvancedMarker
                      key={`adv-poi-${poiVenue.id}`}
                      position={{
                        lat: poiVenue.latitude,
                        lng: poiVenue.longitude,
                      }}
                      zIndex={isPoiHovered ? 999999 : 1001}
                      collisionBehavior={
                        isAllMode
                          ? google.maps.CollisionBehavior
                              .REQUIRED_AND_HIDES_OPTIONAL
                          : undefined
                      }
                      onClick={handlePoiMarkerClick}
                    >
                      <AdvancedPinContent
                        venue={poiVenue}
                        isSelected={
                          activeMarker === poiVenue.id ||
                          selectedVenue?.id === poiVenue.id ||
                          (pinnedFirstVenue?.id === poiVenue.id &&
                            !focusPinRetired)
                        }
                        isHovered={isPoiHovered}
                        isMapperView={isMapperViewActive}
                        onMouseEnter={() => {
                          if (snippetOpen && activeMarker === poiVenue.id) return;
                          setHoveredVenueId(poiVenue.id);
                          setHoveredMarkerId(poiVenue.id);
                        }}
                        onMouseLeave={() => {
                          if (snippetOpen && activeMarker === poiVenue.id) return;
                          setHoveredVenueId("");
                          setHoveredMarkerId(null);
                        }}
                      />
                    </AdvancedMarker>
                  );
                })}

              {/* User location marker */}
              {userLocation && (
                <AdvancedMarker
                  key="adv-user-location"
                  position={{
                    lat: userLocation.latitude,
                    lng: userLocation.longitude,
                  }}
                  zIndex={USER_LOCATION_ZINDEX}
                  title={i18n.get("yourLocation") || "Your current location"}
                >
                  <img src={YouAre} width={30} height={30} alt="" draggable={false} />
                </AdvancedMarker>
              )}

              {/* Desktop snippet InfoWindow — replaces OverlayView nested in legacy Marker */}
              {currentVenueTypePage === 0 &&
                !selectedVenue &&
                snippetVenueAdv &&
                snippetPositionAdv && (
                    <InfoWindow
                      position={snippetPositionAdv}
                      headerDisabled
                      pixelOffset={SNIPPET_PIXEL_OFFSET}
                      className="adv-map-snippet"
                    >
                      <div className="hidden md:block w-[300px]">
                        <div className="map-snippet-wrapper relative bg-bg-brand-secondary-default rounded-lg shadow-md w-full">
                          <style>{`
                            .map-snippet-wrapper button.bg-opacity-30 {
                            right: 52px !important;
                            }`}</style>
                          {/* Grey/unmapped venues get their own close button inside the
                              new grey header (VenueCard); mapped ones keep this one. */}
                          {hasVenueData(snippetVenueAdv) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCloseSnippet();
                              }}
                              className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors"
                              aria-label="Close venue preview"
                              type="button"
                            >
                              <XIcon
                                size={16}
                                weight="bold"
                                className="text-fg-neutral-strong"
                              />
                            </button>
                          )}
                          <button
                            type="button"
                            className="w-full text-left rounded p-0 focus-blue cursor-pointer"
                            aria-label={`View details for ${snippetVenueAdv.name}`}
                          >
                            <VenueCard
                              location={snippetVenueAdv}
                              onOpen={() => handleLoadVenue(snippetVenueAdv)}
                              onSaveVenue={handleSaveVenue}
                              onClose={handleCloseSnippet}
                              savedPlaces={savedPlaces}
                              truncateText={true}
                              index={0}
                              totalCount={locations.length}
                              showMetaInfo={true}
                              showOpeningHours={true}
                              showVariantIndicators={true}
                              showDescription={true}
                              showAddress={false}
                              showActionButtons={true}
                              inMapView={true}
                            />
                          </button>
                        </div>
                      </div>
                    </InfoWindow>
                )}

              {/* Unsupported / error message at the clicked POI point */}
              {/* Selected grey pin under the unsupported-category message. */}
              {poiMessage?.variant === "unsupported" && (
                <AdvancedMarker
                  key="poi-unsupported-pin"
                  position={{ lat: poiMessage.lat, lng: poiMessage.lng }}
                  zIndex={SELECTED_MARKER_ZINDEX}
                  anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
                >
                  <img
                    src={GreyPin}
                    width={PIN_SIZE_SELECTED}
                    height={PIN_SIZE_SELECTED}
                    alt=""
                    draggable={false}
                    style={{ display: "block", pointerEvents: "none" }}
                  />
                </AdvancedMarker>
              )}

              {poiMessage && (
                <InfoWindow
                  position={{ lat: poiMessage.lat, lng: poiMessage.lng }}
                  onClose={() => setPoiMessage(null)}
                  onCloseClick={() => setPoiMessage(null)}
                  headerDisabled
                  pixelOffset={[0, poiMessage.variant === "unsupported" ? -44 : -20]}
                  className="adv-map-snippet"
                >
                  <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-3 rounded-lg w-max max-w-[260px] relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPoiMessage(null);
                      }}
                      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
                      aria-label="Close"
                      type="button"
                    >
                      <XIcon size={14} weight="bold" />
                    </button>
                    <div className="body-s pr-4">{poiMessage.text}</div>
                  </div>
                </InfoWindow>
              )}

              {/* Selected grey pin under the no-data contribution card. */}
              {poiContribution && poiContribution.step !== "options" && (
                <AdvancedMarker
                  key="poi-contribution-pin"
                  position={{ lat: poiContribution.lat, lng: poiContribution.lng }}
                  zIndex={SELECTED_MARKER_ZINDEX}
                  anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
                >
                  <img
                    src={GreyPin}
                    width={PIN_SIZE_SELECTED}
                    height={PIN_SIZE_SELECTED}
                    alt=""
                    draggable={false}
                    style={{ display: "block", pointerEvents: "none" }}
                  />
                </AdvancedMarker>
              )}

              {/* Opt-in contribution flow for a supported POI not yet in our DB.
                  Rendered as an AdvancedMarker (not an InfoWindow) so clicks inside
                  the interactive card don't leak to the map's onClick and close it.
                  Desktop: intro/unsupported are anchored on the map; the "What you
                  can do" options step is a centered modal (rendered below). */}
              {poiContribution && poiContribution.step !== "options" && (
                <AdvancedMarker
                  key="poi-contribution-card"
                  position={{ lat: poiContribution.lat, lng: poiContribution.lng }}
                  zIndex={3_000_000}
                  anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
                  onClick={() => {}}
                >
                  {/* Lift the card clear of the pin so it sits above, not over it. */}
                  <div className="hidden md:block -translate-y-[40px] drop-shadow-xl">
                    <PoiContributionCard
                      step={poiContribution.step}
                      creating={poiContribution.creating}
                      name={poiContribution.name}
                      businessType={poiContribution.businessType}
                      category={poiContribution.category}
                      variant="card"
                      venue={poiContribution.venue}
                      onOpenVenue={handleLoadVenue}
                      onSaveVenue={handleSaveVenue}
                      savedPlaces={savedPlaces}
                      onGetNotified={handleContributionGetNotified}
                      onShowOptions={handleContributionShowOptions}
                      onSelectAction={handleContributionSelectAction}
                      onClose={() => setPoiContribution(null)}
                    />
                  </div>
                </AdvancedMarker>
              )}
            </VisGlMap>
          </APIProvider>
        ) : (
        <>
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "100%",
            }}
            center={mapCenter}
            zoom={16}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={mapOptions} // Pass the dynamic mapOptions here
            //onDragEnd={() => onMapMoved()}
            onClick={(e) => {
              // Intercept native Google POI clicks before the default clear.
              if (
                handlePoiClick(
                  e as google.maps.MapMouseEvent & {
                    placeId?: string;
                    stop?: () => void;
                  },
                )
              ) {
                return;
              }
              setActiveMarker(null);
              setSnippetPosition(null);
              setTooltipVenue(null);
              setHoveredMarkerId(null);
              setHoveredVenueId("");
              setSnippetOpen(false);
              handleClosePoi();
            }}
            onIdle={() => onMapMoved()}
          >
            {locations.map((location, index) => (
              <Marker
                ref={(marker) => {
                  if (marker) {
                    markerRefs.current.set(location.id, marker);
                  }
                }}
                key={`marker-${location.id}-${index}`}
                position={{
                  lat: location.latitude,
                  lng: location.longitude,
                }}
                // title={location.name || "Venue location"}
                zIndex={
                  selectedVenue && selectedVenue.id === location.id
                    ? 1000
                    : index
                }
                icon={{
                  url:
                    selectedVenue && selectedVenue.id === location.id
                      ? getSelectedPinIcon(location)
                      : getPinIcon(location),
                  anchor: new google.maps.Point(15, 30),
                  size:
                    selectedVenue && selectedVenue.id === location.id
                      ? new google.maps.Size(40, 40)
                      : hoveredMarkerId === location.id ||
                          hoveredVenueId === location.id ||
                          activeMarker === location.id
                        ? new google.maps.Size(42, 42)
                        : new google.maps.Size(30, 30),
                  scaledSize:
                    selectedVenue && selectedVenue.id === location.id
                      ? new google.maps.Size(40, 40)
                      : hoveredMarkerId === location.id ||
                          hoveredVenueId === location.id ||
                          activeMarker === location.id
                        ? new google.maps.Size(42, 42)
                        : new google.maps.Size(30, 30),
                }}
                onMouseOver={() => {
                  // Skip only for the pin whose own snippet is open; other
                  // pins should still hover-highlight.
                  if (snippetOpen && activeMarker === location.id) {
                    return;
                  }
                  setTooltipVenue(location);
                  setHoveredVenueId(location.id);
                  setHoveredMarkerId(location.id);
                }}
                onMouseOut={() => {
                  if (snippetOpen && activeMarker === location.id) {
                    return;
                  }
                  setTooltipVenue(null);
                  setHoveredVenueId("");
                  setHoveredMarkerId(null);
                }}
                onClick={() => {
                  // TODO: Panning check commented out since we're not panning
                  // if (isPanning) return;

                  // place_pin_selected — user tapped a search-result map pin
                  // (legacy Marker render path). Mirrors the AdvancedMarker handler.
                  trackEvent("place_pin_selected", {
                    brand_id: location.brandId != null ? String(location.brandId) : "",
                    brand_name: location.brandName ?? "",
                    business_id: location.business?.id?.toString() ?? "",
                    entry_index: index,
                    pin_type: pinTypeForVenue(location),
                    place_name: location?.name ?? "",
                  });

                  // On home page (currentVenueTypePage === 0): show snippet first, then user clicks snippet to load venue
                  // On venue details page (currentVenueTypePage === 1): directly load venue, skip snippet
                  if (
                    currentVenueTypePage === 1 ||
                    props.mapSource === "venuedetails"
                  ) {
                    handleLoadVenue(location);
                  } else {
                    handleShowSnippet(location.id);
                  }
                }}
              >
                {currentVenueTypePage === 0 &&
                !selectedVenue &&
                activeMarker === location.id &&
                markerRefs.current?.get(activeMarker) &&
                snippetPosition
                  ? (() => {
                      return (
                        <>
                          {/* Desktop: Show snippet near pin */}
                          <OverlayView
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            position={{
                              lat: location.latitude,
                              lng: location.longitude,
                            }}
                            getPixelPositionOffset={() =>
                              snippetPosition.offset
                            }
                          >
                            {/* Wrapper with pointer-events-none to not block map */}
                            <div
                              className={`hidden md:block w-[300px] ${snippetPosition.className}`}
                              style={{ pointerEvents: "none" }}
                            >
                              <div
                                className="map-snippet-wrapper relative bg-bg-brand-secondary-default rounded-lg shadow-md w-full"
                                style={{ pointerEvents: "auto" }}
                              >
                                <style>{`
                                  .map-snippet-wrapper button.bg-opacity-30 {
                                  right: 52px !important;
                                  }`}</style>

                                {/* Grey/unmapped venues get their own close button inside the
                                    new grey header (VenueCard); mapped ones keep this one. */}
                                {hasVenueData(location) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCloseSnippet();
                                    }}
                                    className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                                    aria-label="Close venue preview"
                                    type="button"
                                  >
                                    <XIcon
                                      size={16}
                                      weight="bold"
                                      className="text-fg-neutral-strong"
                                    />
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className="w-full text-left rounded p-0 focus-blue pointer-events-auto cursor-pointer"
                                  aria-label={`View details for ${location.name}`}
                                >
                                  <VenueCard
                                    location={location}
                                    onOpen={() => handleLoadVenue(location)}
                                    onSaveVenue={handleSaveVenue}
                                    onClose={handleCloseSnippet}
                                    savedPlaces={savedPlaces}
                                    truncateText={true}
                                    index={index}
                                    totalCount={locations.length}
                                    showMetaInfo={true}
                                    showOpeningHours={true}
                                    showVariantIndicators={true}
                                    showDescription={true}
                                    showAddress={false}
                                    showActionButtons={true}
                                    inMapView={true}
                                  />
                                </button>
                              </div>
                            </div>
                          </OverlayView>
                        </>
                      );
                    })()
                  : null}
              </Marker>
            ))}
            {userLocation && (
              <Marker
                key={"userLocation"}
                position={{
                  lat: userLocation.latitude,
                  lng: userLocation.longitude,
                }}
                title={i18n.get("yourLocation") || "Your current location"}
                zIndex={USER_LOCATION_ZINDEX}
                icon={{
                  url: YouAre,
                  anchor: new google.maps.Point(15, 30),
                  size: new google.maps.Size(30, 30),
                  scaledSize: new google.maps.Size(30, 30),
                }}
              ></Marker>
            )}

            {/* Venues resolved from Google POI clicks / suggest (persisted across clicks).
                Skip any that are already rendered as a normal search pin. */}
            {resolvedPois
              .filter(
                (v) =>
                  !locations.some(
                    (l) =>
                      (v.placeId && l.placeId === v.placeId) || l.id === v.id,
                  ),
              )
              .map((poiVenue) => (
              <Marker
                key={`poi-${poiVenue.id}`}
                position={{
                  lat: poiVenue.latitude,
                  lng: poiVenue.longitude,
                }}
                zIndex={1001}
                icon={{
                  url:
                    activeMarker === poiVenue.id
                      ? getSelectedPinIcon(poiVenue)
                      : getPinIcon(poiVenue),
                  anchor: new google.maps.Point(15, 30),
                  size: new google.maps.Size(40, 40),
                  scaledSize: new google.maps.Size(40, 40),
                }}
                onClick={() => {
                  // Mirror the regular-pin behaviour: on the venue-detail page
                  // load the venue directly (no snippet), on the search page show
                  // the snippet first.
                  if (
                    currentVenueTypePage === 1 ||
                    props.mapSource === "venuedetails"
                  ) {
                    handleLoadVenue(poiVenue);
                  } else {
                    setActiveMarker(poiVenue.id);
                    setSnippetPosition(getSnippetPosition(poiVenue));
                    setSnippetOpen(true);
                  }
                }}
              >
                {currentVenueTypePage === 0 &&
                !selectedVenue &&
                activeMarker === poiVenue.id &&
                snippetPosition ? (
                  <OverlayView
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    position={{
                      lat: poiVenue.latitude,
                      lng: poiVenue.longitude,
                    }}
                    getPixelPositionOffset={() => snippetPosition.offset}
                  >
                    <div
                      className={`hidden md:block w-[300px] ${snippetPosition.className}`}
                      style={{ pointerEvents: "none" }}
                    >
                      <div
                        className="map-snippet-wrapper relative bg-bg-brand-secondary-default rounded-lg shadow-md w-full"
                        style={{ pointerEvents: "auto" }}
                      >
                        {/* Grey/unmapped venues get their own close button inside the
                            new grey header (VenueCard); mapped ones keep this one. */}
                        {hasVenueData(poiVenue) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseSnippet();
                            }}
                            className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                            aria-label="Close venue preview"
                            type="button"
                          >
                            <XIcon
                              size={16}
                              weight="bold"
                              className="text-fg-neutral-strong"
                            />
                          </button>
                        )}
                        <button
                          type="button"
                          className="w-full text-left rounded p-0 focus-blue pointer-events-auto cursor-pointer"
                          aria-label={`View details for ${poiVenue.name}`}
                        >
                          <VenueCard
                            location={poiVenue}
                            onOpen={() => handleLoadVenue(poiVenue)}
                            onSaveVenue={handleSaveVenue}
                            onClose={handleCloseSnippet}
                            savedPlaces={savedPlaces}
                            truncateText={true}
                            index={0}
                            totalCount={1}
                            showMetaInfo={true}
                            showOpeningHours={true}
                            showVariantIndicators={true}
                            showDescription={true}
                            showAddress={false}
                            showActionButtons={true}
                            inMapView={true}
                          />
                        </button>
                      </div>
                    </div>
                  </OverlayView>
                ) : null}
              </Marker>
            ))}

            {/* Unsupported / error message card at the clicked point */}
            {poiMessage && (
              <OverlayView
                position={{ lat: poiMessage.lat, lng: poiMessage.lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={(width, height) => ({
                  x: -(width / 2),
                  y: -height - 20,
                })}
              >
                <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-3 rounded-lg shadow-lg w-max max-w-[260px] relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPoiMessage(null);
                    }}
                    className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
                    aria-label="Close"
                    type="button"
                  >
                    <XIcon size={14} weight="bold" />
                  </button>
                  <div className="body-s pr-4">{poiMessage.text}</div>
                </div>
              </OverlayView>
            )}

            {/* Hover tooltip - hidden only for the venue whose snippet is open */}
            {(() => {
              const displayVenue =
                tooltipVenue ||
                (hoveredVenueId
                  ? locations.find((l) => l.id === hoveredVenueId)
                  : null);
              if (snippetOpen && displayVenue?.id === activeMarker) return null;
              return displayVenue ? (
                <OverlayView
                  position={{
                    lat: displayVenue.latitude,
                    lng: displayVenue.longitude,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => ({
                    x: -(width / 2),
                    y: -height - 50,
                  })}
                >
                  <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-2 rounded-lg shadow-lg pointer-events-none w-max max-w-[400px]">
                    <div className="label-m-medium whitespace-nowrap">
                      {displayVenue.name}
                    </div>
                  </div>
                </OverlayView>
              ) : null;
            })()}
          </GoogleMap>

          {/* TODO: Invisible overlay commented out since we're not panning */}
          {/* {isPanning && (
<div
className="absolute inset-0 z-[1000] cursor-wait"
style={{ pointerEvents:'all' }}
/>
)} */}
        </>
        )
      )}
      {/* Resolving a tapped Google place: small non-blocking loading pill. */}
      {poiResolving && (
        <div className="absolute left-0 right-0 top-2 flex items-center justify-center pointer-events-none z-20">
          <div className="flex items-center gap-2 bg-bg-brand-secondary-default border border-border-token-neutral-minimal rounded-full px-4 py-2 shadow-lg">
            <span className="w-4 h-4 rounded-full border-2 border-fg-brand-subtle border-t-transparent animate-spin" />
            <span className="label-s-medium text-fg-neutral-strong">
              {i18n.get("loadingPlace")}
            </span>
          </div>
        </div>
      )}

      {showAllPlacesHint && (
        <AllPlacesHint onHeightChange={setAllPlacesHintHeight} />
      )}

      {/* Top-of-map controls. The mode toggle and "Refresh map" share one row
          so the map keeps as much vertical space as possible; on narrow screens
          they wrap rather than collide. */}
      {(showMapModeToggle || showRefreshMapButton) && (
        <div
          className={cn(
            "absolute left-0 right-0 top-2 px-2 flex flex-wrap items-center gap-2 pointer-events-none",
            // Toggle left / refresh right when both are up; refresh alone stays centred.
            showMapModeToggle ? "justify-between" : "justify-center",
          )}
          style={
            allPlacesHintHeight
              ? { top: allPlacesHintHeight + 8 }
              : undefined
          }
        >
          {showMapModeToggle && (
            <div className="pointer-events-auto">
              <MapModeToggle
                mapMode={mapMode}
                onChange={changeMapMode}
                modes={["sociability", "all"]}
              />
            </div>
          )}

          {/* Show nearby. Also available while a venue detail is open (small
              map) — re-centers the results on the current map viewport in
              place, without closing the open venue detail. */}
          {showRefreshMapButton && (
          <StandardButton
            variant="secondary"
            size="auto"
            // Sits just inside the toggle's height (42px) rather than matching it.
            className="pointer-events-auto rounded-[8px] min-h-0 h-9 px-3 py-1.5 label-m-medium shadow-md bg-bg-brand-secondary-default"
            onClick={() => {
              // Get current map center
              const currentCenter = mapRef.getCenter();
              const currentLat = currentCenter.lat();
              const currentLng = currentCenter.lng();

              // Update mapCenter atom to keep it in sync
              // This ensures geocoding uses the correct location when user types a new search term
              setMapCenter({ lat: currentLat, lng: currentLng });

              const currentBounds = mapRef.getBounds();
              const currentNe = currentBounds?.getNorthEast();
              const currentSw = currentBounds?.getSouthWest();

              setSearchPageParams((prev) => {
                // Carry the active map mode as-is (sociability/declutter both
                // need mapping_status "mapped" to exclude grey/unmapped pins;
                // "all" — including when map_mode is unset — does not). Only
                // when the POI-click feature (and its toggle) is enabled.
                const currentMode = prev.map_mode ?? "sociability";
                const params = {
                  ...prev,
                  latitude: currentLat.toString(),
                  longitude: currentLng.toString(),
                  userlatitude: userLocation?.latitude.toString() || "",
                  userlongitude: userLocation?.longitude.toString() || "",
                  ...(enableGooglePoiClick
                    ? {
                        map_mode: currentMode,
                        mapping_status:
                          currentMode === "all" ? undefined : "mapped",
                      }
                    : {}),
                  searchThisAreaClicked: "yes",
                  autoZoom: "no", // user has chosen this viewport — never let fitBounds zoom out
                  zoom: mapRef?.getZoom().toString() || "15",
                  saved_place_id: savedPlaceIdToView,
                  saved_place_user_id: savedPlaceByUserIdToView,
                  saved_place_sortby: savedPlaceSortByToView,
                  unixTimestamp: Math.floor(Date.now() / 1000),
                  includeGoogleFallback: "false",
                  ...viewportBoxParams(currentNe, currentSw),
                };
                // Clear expand_radius specifically — other search types (brand, venue_name, etc.) should persist
                if (prev.search_type === "expand_radius") {
                  delete params.search_type;
                  delete params.search_value;
                }
                return params;
              });
            }}
          >
            <span className="w-4 h-4 relative mr-1 flex items-center justify-center">
              {isBackgroundReloading ? (
                <span className="w-4 h-4 rounded-full border-2 border-fg-brand-subtle border-t-transparent animate-spin" />
              ) : (
                <FiMapPin size={16} />
              )}
            </span>
            {searchPageParams.search
              ? i18n.get("searchThisArea")
              : i18n.get("refreshMap")}
          </StandardButton>
          )}
        </div>
      )}
      {/* Goto current location, +, - */}
      <div className="absolute right-0 bottom-0 top-0 flex flex-col items-end justify-end pointer-events-none">
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            const currentZoom = mapRef.getZoom() ?? 0;
            if (currentZoom < 17) {
              const newZoom = currentZoom + 1;
              mapRef.setZoom(newZoom);
              // map_zoomed — in-map zoom-in control. BasicMapView is shared across
              // /search, saved-place detail & venue details (no single page scope),
              // and the event carries no page-distinguishing prop → fire the primitive.
              trackEvent("map_zoomed", {
                zoom_direction: "in",
                zoom_level: newZoom,
              });
            }
          }}
          className="p-2 mb-1 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold pointer-events-auto"
          aria-label={i18n.get("zoomIn") || "Zoom in"}
        >
          <FaPlus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            const currentZoom = mapRef.getZoom() ?? 0;
            if (currentZoom > 0) {
              const newZoom = currentZoom - 1;
              mapRef.setZoom(newZoom);
              // map_zoomed — in-map zoom-out control (mirrors the zoom-in handler).
              trackEvent("map_zoomed", {
                zoom_direction: "out",
                zoom_level: newZoom,
              });
            }
          }}
          className="p-2 mb-8 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold pointer-events-auto"
          aria-label={i18n.get("zoomOut") || "Zoom out"}
        >
          <FaMinus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            console.log("Requesting location");
            // map_location_centered — "go to current location" map control. Stub
            // payload; shared map widget → fire the primitive.
            trackEvent("map_location_centered", {});

            // Conditional logic based on locationError
            if (locationError) {
              setIsLocationModalOpen(true);
            } else {
              goToUserLocationRequestedRef.current = true;
              requestUserLocation();
            }
          }}
          className="shadow-md p-2 mb-4 mr-2 flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold pointer-events-auto"
          aria-label={
            i18n.get("goToCurrentLocation") || "Go to current location"
          }
        >
          <TbCurrentLocation
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
      </div>
      {(isLoading || locationLoading) && (
        <Loader
          includeGoogleFallback={
            searchPageParams.includeGoogleFallback === "true"
          }
        />
      )}

      {/* Mobile: Fixed bottom snippet */}
      {currentVenueTypePage === 0 &&
        !selectedVenue &&
        activeMarker &&
        (() => {
          // Include resolved POI venues (not part of `locations`).
          const location =
            resolvedPois.find((v) => v.id === activeMarker) ||
            locations.find((l) => l.id === activeMarker);
          const index = locations.findIndex((l) => l.id === activeMarker);
          if (!location) return null;

          return (
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-[1000] p-3 pb-4 animate-slide-up pointer-events-none">
              <style>{`
 @keyframes slide-up {
 from {
 transform: translateY(100%);
 }
 to {
 transform: translateY(0);
 }
 }
 .animate-slide-up {
 animation: slide-up 0.3s ease-out;
 }
 .mobile-snippet-wrapper button.bg-opacity-30 {
 right: 52px !important;
 }
 .mobile-snippet-container {
 max-height: 280px;
 overflow: hidden;
 }
 .mobile-snippet-wrapper figure {
 aspect-ratio: 16 / 7 !important;
 max-height: 140px !important;
 }
 .mobile-snippet-wrapper figure img {
 object-fit: cover !important;
 }`}</style>

              <div className="mobile-snippet-container relative">
                {/* Grey/unmapped venues get their own close button inside the
                    new grey header (VenueCard); mapped ones keep this one. */}
                {hasVenueData(location) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseSnippet();
                    }}
                    className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                    aria-label="Close venue preview"
                    type="button"
                  >
                    <XIcon
                      size={16}
                      weight="bold"
                      className="text-fg-neutral-strong"
                    />
                  </button>
                )}

                <button
                  type="button"
                  className="w-full text-left rounded p-0 focus-blue mobile-snippet-wrapper cursor-pointer pointer-events-auto"
                  aria-label={`View details for ${location.name}`}
                >
                  <VenueCard
                    location={location}
                    onOpen={() => handleLoadVenue(location)}
                    onSaveVenue={handleSaveVenue}
                    onClose={handleCloseSnippet}
                    showActionButtons={true}
                    savedPlaces={savedPlaces}
                    truncateText={true}
                    index={index}
                    totalCount={locations.length}
                    showMetaInfo={true}
                    showOpeningHours={true}
                    showVariantIndicators={true}
                    showDescription={true}
                    showAddress={false}
                    inMapView={true}
                  />
                </button>
              </div>
            </div>
          );
        })()}

      {/* "What you can do" (intro/unsupported stay anchored on the map above). */}
      {poiContribution && poiContribution.step === "options" && (
        <ContributionOptionsModal
          creating={poiContribution.creating}
          onGetNotified={handleContributionGetNotified}
          onShowOptions={handleContributionShowOptions}
          onSelectAction={handleContributionSelectAction}
          onClose={() => setPoiContribution(null)}
          onBack={handleContributionBack}
        />
      )}

      {poiContribution && poiContribution.step !== "options" && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-[1000] p-3 pb-4 animate-slide-up">
          <style>{`
@keyframes slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
.animate-slide-up { animation: slide-up 0.3s ease-out; }`}</style>
          <PoiContributionCard
            step={poiContribution.step}
            creating={poiContribution.creating}
            name={poiContribution.name}
            category={poiContribution.category}
            variant="sheet"
            venue={poiContribution.venue}
            onOpenVenue={handleLoadVenue}
            onSaveVenue={handleSaveVenue}
            savedPlaces={savedPlaces}
            onGetNotified={handleContributionGetNotified}
            onShowOptions={handleContributionShowOptions}
            onSelectAction={handleContributionSelectAction}
            onClose={() => setPoiContribution(null)}
            onBack={handleContributionBack}
          />
        </div>
      )}

      <ModalWithAction
        isModalOpen={isLocationModalOpen}
        title={i18n.get("locationSettingsRequired")}
        subtitle={i18n.get("locationSettingsRequiredMessage")}
        cancelButtonTitle={""}
        actionButtonTitle={i18n.get("ok")}
        onClose={() => {
          setIsLocationModalOpen(false);
        }}
        onClick={handleSetLocation}
        type="default"
        actionButtonType="default"
      />

      {/* Saved Places Modal */}
      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={locations.find(
                (l) => l.business.id === locationForSavedPlaces,
              )}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={locations.find(
                (l) => l.business.id === locationForSavedPlaces,
              )}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

export default BasicMapView;
